/**
 * Golden Quiz - Question Bank
 *
 * 7 Categories × 60 Questions Each = 420 Total Questions
 * Each category: 20 Easy + 20 Medium + 20 Hard
 *
 * Categories:
 * 1. South African History & Culture
 * 2. Python Programming
 * 3. AI Literacy
 * 4. General Science
 * 5. Mathematics
 * 6. Current Events
 * 7. Geography
 */

export const categories = [
  {
    id: 'south-african-history',
    name: 'South African History & Culture',
    icon: '🇿🇦',
    description: 'Test your knowledge of South African history, culture, and heritage',
    color: '#D4AF37'
  },
  {
    id: 'python-programming',
    name: 'Python Programming',
    icon: '🐍',
    description: 'Programming concepts, syntax, and problem-solving with Python',
    color: '#3776AB'
  },
  {
    id: 'ai-literacy',
    name: 'AI Literacy',
    icon: '🤖',
    description: 'Understanding AI, critical evaluation, and responsible use',
    color: '#FF6B6B'
  },
  {
    id: 'general-science',
    name: 'General Science',
    icon: '🔬',
    description: 'Biology, chemistry, physics, and scientific thinking',
    color: '#4ECDC4'
  },
  {
    id: 'mathematics',
    name: 'Mathematics',
    icon: '📐',
    description: 'Mathematical concepts, problem-solving, and logical reasoning',
    color: '#95E1D3'
  },
  {
    id: 'current-events',
    name: 'Current Events',
    icon: '📰',
    description: 'Recent news, developments, and global awareness (2024-2026)',
    color: '#F38181'
  },
  {
    id: 'geography',
    name: 'Geography',
    icon: '🌍',
    description: 'World geography, African geography, and spatial awareness',
    color: '#AA96DA'
  }
];

// ============================================================================
// CATEGORY 1: SOUTH AFRICAN HISTORY & CULTURE
// ============================================================================

const southAfricanHistoryQuestions = [
  // EASY QUESTIONS (1-20)
  {
    id: 'sah-e-1',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'In what year did South Africa hold its first democratic elections?',
    options: ['1990', '1992', '1994', '1996'],
    correctAnswer: 2,
    explanation: 'South Africa held its first democratic elections on April 27, 1994, marking the end of apartheid. This day is now celebrated as Freedom Day.'
  },
  {
    id: 'sah-e-2',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'Who was South Africa\'s first democratically elected president?',
    options: ['Thabo Mbeki', 'Nelson Mandela', 'F.W. de Klerk', 'Desmond Tutu'],
    correctAnswer: 1,
    explanation: 'Nelson Mandela became South Africa\'s first Black president and first democratically elected president in 1994, serving until 1999.'
  },
  {
    id: 'sah-e-3',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'How many official languages does South Africa have?',
    options: ['2', '9', '11', '15'],
    correctAnswer: 2,
    explanation: 'South Africa has 11 official languages: isiZulu, isiXhosa, Afrikaans, English, Sepedi, Setswana, Sesotho, Xitsonga, siSwati, Tshivenda, and isiNdebele.'
  },
  {
    id: 'sah-e-4',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'What is the capital city of South Africa?',
    options: ['Johannesburg', 'Cape Town', 'Pretoria', 'All three'],
    correctAnswer: 3,
    explanation: 'South Africa has three capital cities: Pretoria (executive), Cape Town (legislative), and Bloemfontein (judicial).'
  },
  {
    id: 'sah-e-5',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'What does "ubuntu" mean?',
    options: ['Freedom', 'I am because we are', 'Peace', 'Ubuntu is a Linux distribution'],
    correctAnswer: 1,
    explanation: 'Ubuntu is a Nguni Bantu term meaning "I am because we are" - emphasizing humanity, community, and interconnectedness.'
  },
  {
    id: 'sah-e-6',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'When is Freedom Day celebrated in South Africa?',
    options: ['April 27', 'June 16', 'December 16', 'March 21'],
    correctAnswer: 0,
    explanation: 'Freedom Day is celebrated on April 27, commemorating the first democratic elections in 1994.'
  },
  {
    id: 'sah-e-7',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'What is the traditional South African braai?',
    options: ['A dance', 'A barbecue', 'A song', 'A sport'],
    correctAnswer: 1,
    explanation: 'A braai is a traditional South African barbecue, a central part of social culture across all communities.'
  },
  {
    id: 'sah-e-8',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'Which ocean borders South Africa to the south?',
    options: ['Atlantic Ocean', 'Indian Ocean', 'Both Atlantic and Indian', 'Pacific Ocean'],
    correctAnswer: 2,
    explanation: 'South Africa is bordered by both the Atlantic Ocean (west coast) and Indian Ocean (east coast).'
  },
  {
    id: 'sah-e-9',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'What is the largest city in South Africa by population?',
    options: ['Cape Town', 'Durban', 'Johannesburg', 'Pretoria'],
    correctAnswer: 2,
    explanation: 'Johannesburg is South Africa\'s largest city by population and is the economic hub of the country.'
  },
  {
    id: 'sah-e-10',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'When is Youth Day celebrated in South Africa?',
    options: ['April 27', 'June 16', 'September 24', 'December 16'],
    correctAnswer: 1,
    explanation: 'Youth Day is celebrated on June 16, commemorating the Soweto Uprising of 1976 when students protested against Afrikaans as the medium of instruction.'
  },
  {
    id: 'sah-e-11',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'What is the Rainbow Nation?',
    options: ['A nature reserve', 'A nickname for South Africa', 'A political party', 'A sports team'],
    correctAnswer: 1,
    explanation: 'The Rainbow Nation is a term coined by Archbishop Desmond Tutu to describe post-apartheid South Africa\'s multicultural diversity.'
  },
  {
    id: 'sah-e-12',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'Which South African won the Nobel Peace Prize in 1993?',
    options: ['Nelson Mandela only', 'F.W. de Klerk only', 'Both Mandela and de Klerk', 'Desmond Tutu'],
    correctAnswer: 2,
    explanation: 'Both Nelson Mandela and F.W. de Klerk jointly won the Nobel Peace Prize in 1993 for their work in peacefully ending apartheid.'
  },
  {
    id: 'sah-e-13',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'What is the national flower of South Africa?',
    options: ['Rose', 'Protea', 'Sunflower', 'Jacaranda'],
    correctAnswer: 1,
    explanation: 'The King Protea (Protea cynaroides) is South Africa\'s national flower, representing beauty, courage, and diversity.'
  },
  {
    id: 'sah-e-14',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'What was apartheid?',
    options: ['A system of racial segregation', 'A political party', 'A type of government', 'A cultural movement'],
    correctAnswer: 0,
    explanation: 'Apartheid was a system of institutionalized racial segregation and discrimination in South Africa from 1948 to 1994.'
  },
  {
    id: 'sah-e-15',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'Which country borders South Africa to the north?',
    options: ['Mozambique', 'Zimbabwe', 'Botswana', 'All of the above'],
    correctAnswer: 3,
    explanation: 'South Africa borders Zimbabwe, Botswana, Mozambique, Namibia, Lesotho, and Eswatini (Swaziland).'
  },
  {
    id: 'sah-e-16',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'What is Robben Island famous for?',
    options: ['Beach resort', 'Prison where Mandela was held', 'Diamond mine', 'Wildlife sanctuary'],
    correctAnswer: 1,
    explanation: 'Robben Island is famous as the prison where Nelson Mandela was held for 18 of his 27 years in prison. It\'s now a museum and World Heritage Site.'
  },
  {
    id: 'sah-e-17',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'What is the currency of South Africa?',
    options: ['Dollar', 'Pound', 'Rand', 'Shilling'],
    correctAnswer: 2,
    explanation: 'The South African Rand (ZAR) is the official currency, introduced in 1961.'
  },
  {
    id: 'sah-e-18',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'Which South African leader spent 27 years in prison?',
    options: ['Steve Biko', 'Nelson Mandela', 'Walter Sisulu', 'Oliver Tambo'],
    correctAnswer: 1,
    explanation: 'Nelson Mandela spent 27 years in prison (1962-1990) for his anti-apartheid activism before becoming president.'
  },
  {
    id: 'sah-e-19',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'What is the name of South Africa\'s national anthem?',
    options: ['Nkosi Sikelel\' iAfrika', 'Die Stem', 'Both combined', 'God Bless Africa'],
    correctAnswer: 2,
    explanation: 'South Africa\'s national anthem combines "Nkosi Sikelel\' iAfrika" and "Die Stem van Suid-Afrika," symbolizing unity.'
  },
  {
    id: 'sah-e-20',
    category: 'south-african-history',
    difficulty: 'easy',
    question: 'When is Heritage Day celebrated?',
    options: ['April 27', 'June 16', 'September 24', 'December 16'],
    correctAnswer: 2,
    explanation: 'Heritage Day is celebrated on September 24, celebrating South Africa\'s diverse cultural heritage. It\'s also known as National Braai Day.'
  },

  // MEDIUM QUESTIONS (21-40)
  {
    id: 'sah-m-1',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What was the Soweto Uprising primarily protesting against?',
    options: ['Voting rights', 'Afrikaans as medium of instruction', 'Pass laws', 'Land dispossession'],
    correctAnswer: 1,
    explanation: 'The Soweto Uprising of June 16, 1976, began as a student protest against the enforcement of Afrikaans as the medium of instruction in schools.'
  },
  {
    id: 'sah-m-2',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'Who was the first Black person to win an Olympic gold medal for South Africa?',
    options: ['Caster Semenya', 'Josia Thugwane', 'Lucas Radebe', 'Wayde van Niekerk'],
    correctAnswer: 1,
    explanation: 'Josia Thugwane won gold in the marathon at the 1996 Atlanta Olympics, becoming the first Black South African to win Olympic gold.'
  },
  {
    id: 'sah-m-3',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What were the "pass laws" under apartheid?',
    options: ['Education requirements', 'Laws requiring Black people to carry identity documents', 'Voting restrictions', 'Property ownership rules'],
    correctAnswer: 1,
    explanation: 'Pass laws required Black South Africans to carry passes (identity documents) at all times and restricted their movement, particularly to "white" areas.'
  },
  {
    id: 'sah-m-4',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What was the Sharpeville Massacre?',
    options: ['A battle in 1899', 'Police shooting of protesters in 1960', 'A mining disaster', 'A train accident'],
    correctAnswer: 1,
    explanation: 'On March 21, 1960, police opened fire on a crowd of protesters in Sharpeville, killing 69 people. This day is now commemorated as Human Rights Day.'
  },
  {
    id: 'sah-m-5',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'Who founded the African National Congress (ANC)?',
    options: ['Nelson Mandela', 'John Langalibalele Dube', 'Oliver Tambo', 'Walter Sisulu'],
    correctAnswer: 1,
    explanation: 'The ANC was founded in 1912, with John Langalibalele Dube as its first president. It is Africa\'s oldest liberation movement.'
  },
  {
    id: 'sah-m-6',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What is the significance of the Great Trek?',
    options: ['A military campaign', 'Boer migration inland from Cape Colony', 'Mining expedition', 'Trade route establishment'],
    correctAnswer: 1,
    explanation: 'The Great Trek (1830s-1840s) was the migration of Dutch-speaking settlers (Voortrekkers) from the Cape Colony into the interior of South Africa.'
  },
  {
    id: 'sah-m-7',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'Who was Steve Biko?',
    options: ['A musician', 'Black Consciousness Movement leader', 'Sports figure', 'Political prisoner'],
    correctAnswer: 1,
    explanation: 'Steve Biko was a prominent anti-apartheid activist and founder of the Black Consciousness Movement. He died in police custody in 1977.'
  },
  {
    id: 'sah-m-8',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What was the Group Areas Act?',
    options: ['Voting law', 'Law segregating residential areas by race', 'Education policy', 'Economic regulation'],
    correctAnswer: 1,
    explanation: 'The Group Areas Act (1950) forcibly segregated residential areas by race, leading to forced removals and the destruction of communities like District Six.'
  },
  {
    id: 'sah-m-9',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'Which South African author won the Nobel Prize for Literature?',
    options: ['Nadine Gordimer', 'J.M. Coetzee', 'Both Gordimer and Coetzee', 'Alan Paton'],
    correctAnswer: 2,
    explanation: 'Both Nadine Gordimer (1991) and J.M. Coetzee (2003) won the Nobel Prize for Literature.'
  },
  {
    id: 'sah-m-10',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What was the Rivonia Trial?',
    options: ['Mining dispute', 'Treason trial of Mandela and others', 'Land claim case', 'Sports corruption case'],
    correctAnswer: 1,
    explanation: 'The Rivonia Trial (1963-1964) was when Nelson Mandela and other ANC leaders were tried for sabotage. Mandela\'s famous "I am prepared to die" speech was delivered here.'
  },
  {
    id: 'sah-m-11',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'Who was the first female president of the ANC?',
    options: ['Winnie Mandela', 'Albertina Sisulu', 'Nkosazana Dlamini-Zuma', 'Helen Suzman'],
    correctAnswer: 2,
    explanation: 'Nkosazana Dlamini-Zuma became the first female president of the ANC in 2012 when elected as chairperson of the AU Commission.'
  },
  {
    id: 'sah-m-12',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What were Bantustans?',
    options: ['Military bases', 'Homelands for Black South Africans under apartheid', 'Mining towns', 'Universities'],
    correctAnswer: 1,
    explanation: 'Bantustans (or homelands) were territories set aside for Black South Africans under apartheid, designed to deny them South African citizenship.'
  },
  {
    id: 'sah-m-13',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What was the Freedom Charter?',
    options: ['Independence declaration', 'ANC policy document adopted in 1955', 'Peace treaty', 'Constitution'],
    correctAnswer: 1,
    explanation: 'The Freedom Charter, adopted in 1955 at the Congress of the People in Kliptown, outlined a vision for a democratic South Africa.'
  },
  {
    id: 'sah-m-14',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'Who was the last president of apartheid South Africa?',
    options: ['P.W. Botha', 'F.W. de Klerk', 'B.J. Vorster', 'Nelson Mandela'],
    correctAnswer: 1,
    explanation: 'F.W. de Klerk was the last president of apartheid South Africa (1989-1994). He initiated the negotiations that led to democracy.'
  },
  {
    id: 'sah-m-15',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What is the significance of Marikana in recent South African history?',
    options: ['Gold discovery', 'Police shooting of miners in 2012', 'Political rally', 'Sports event'],
    correctAnswer: 1,
    explanation: 'The Marikana massacre (August 16, 2012) saw police shoot and kill 34 striking mineworkers, the deadliest use of force by South African security forces since 1994.'
  },
  {
    id: 'sah-m-16',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'Who was Oliver Tambo?',
    options: ['Sports hero', 'ANC president in exile', 'Supreme Court judge', 'Business leader'],
    correctAnswer: 1,
    explanation: 'Oliver Tambo was president of the ANC from 1967 to 1991, leading the organization from exile and maintaining international anti-apartheid support.'
  },
  {
    id: 'sah-m-17',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What was the significance of the 1820 Settlers?',
    options: ['First Dutch settlers', 'British settlers who arrived at the Cape', 'Indian workers', 'Portuguese explorers'],
    correctAnswer: 1,
    explanation: 'The 1820 Settlers were approximately 4,000 British settlers who arrived in the Eastern Cape, significantly influencing the region\'s culture and demographics.'
  },
  {
    id: 'sah-m-18',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What was the Defiance Campaign of 1952?',
    options: ['Armed struggle', 'Mass civil disobedience against apartheid laws', 'Sports boycott', 'Economic sanctions'],
    correctAnswer: 1,
    explanation: 'The Defiance Campaign was a mass civil disobedience campaign organized by the ANC and SAIC to resist apartheid laws through non-violent protest.'
  },
  {
    id: 'sah-m-19',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'Who were the San people?',
    options: ['Dutch settlers', 'Indigenous hunter-gatherers of southern Africa', 'British colonists', 'Indian traders'],
    correctAnswer: 1,
    explanation: 'The San (also called Bushmen) are the indigenous hunter-gatherer people of southern Africa, with a history dating back thousands of years.'
  },
  {
    id: 'sah-m-20',
    category: 'south-african-history',
    difficulty: 'medium',
    question: 'What was the significance of the Voortrekker Monument?',
    options: ['Prison', 'Monument honoring Afrikaner pioneers', 'Government building', 'Museum of democracy'],
    correctAnswer: 1,
    explanation: 'The Voortrekker Monument in Pretoria commemorates the Voortrekkers who left the Cape Colony during the Great Trek. It remains a controversial symbol.'
  },

  // HARD QUESTIONS (41-60)
  {
    id: 'sah-h-1',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'Who was the first Black lawyer in South Africa?',
    options: ['Nelson Mandela', 'Oliver Tambo', 'Alfred Mangena', 'Pixley ka Isaka Seme'],
    correctAnswer: 2,
    explanation: 'Alfred Mangena became the first Black lawyer in South Africa in 1910, paving the way for future Black legal professionals.'
  },
  {
    id: 'sah-h-2',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Bambatha Rebellion of 1906?',
    options: ['Workers\' strike', 'Zulu uprising against poll tax', 'Student protest', 'Women\'s march'],
    correctAnswer: 1,
    explanation: 'The Bambatha Rebellion was a Zulu uprising led by Chief Bambatha kaMancinza against the poll tax imposed by the Natal colonial government. It was violently suppressed.'
  },
  {
    id: 'sah-h-3',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Native Land Act of 1913?',
    options: ['Voting law', 'Law restricting Black land ownership to 7% of South Africa', 'Education policy', 'Labor regulation'],
    correctAnswer: 1,
    explanation: 'The Native Land Act of 1913 restricted Black South Africans to owning land in designated reserves, which comprised only 7% (later 13%) of the country.'
  },
  {
    id: 'sah-h-4',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'Who was Charlotte Maxeke?',
    options: ['First Black female lawyer', 'First Black woman to earn a university degree', 'First Black female doctor', 'First Black female teacher'],
    correctAnswer: 1,
    explanation: 'Charlotte Maxeke was the first Black South African woman to earn a university degree (1901) and was a prominent activist and women\'s rights advocate.'
  },
  {
    id: 'sah-h-5',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Battle of Isandlwana?',
    options: ['Boer War battle', 'Zulu victory over British forces in 1879', 'World War I battle', 'Tribal conflict'],
    correctAnswer: 1,
    explanation: 'The Battle of Isandlwana (January 22, 1879) was a major Zulu victory over British forces during the Anglo-Zulu War, one of the worst British defeats in colonial history.'
  },
  {
    id: 'sah-h-6',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Tricameral Parliament established in 1984?',
    options: ['Three-house parliament excluding Black Africans', 'All-inclusive parliament', 'Judicial system', 'Provincial government'],
    correctAnswer: 0,
    explanation: 'The Tricameral Parliament gave limited representation to Coloureds and Indians in separate chambers but excluded Black Africans entirely, sparking major protests.'
  },
  {
    id: 'sah-h-7',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'Who founded the Inkatha Freedom Party?',
    options: ['Nelson Mandela', 'Mangosuthu Buthelezi', 'Desmond Tutu', 'Walter Sisulu'],
    correctAnswer: 1,
    explanation: 'Mangosuthu Buthelezi founded the Inkatha Freedom Party in 1975, which became a major political force, particularly in KwaZulu-Natal.'
  },
  {
    id: 'sah-h-8',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Immorality Act of 1950?',
    options: ['Censorship law', 'Law prohibiting sexual relations between races', 'Voting restriction', 'Labor regulation'],
    correctAnswer: 1,
    explanation: 'The Immorality Act prohibited sexual relations and marriage between white people and people of other races, a cornerstone of apartheid social engineering.'
  },
  {
    id: 'sah-h-9',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'Who was Ruth First?',
    options: ['Anti-apartheid activist killed by letter bomb', 'First female MP', 'Sports pioneer', 'Artist'],
    correctAnswer: 0,
    explanation: 'Ruth First was a prominent anti-apartheid activist, journalist, and scholar who was assassinated by a letter bomb in Mozambique in 1982. She was married to Joe Slovo.'
  },
  {
    id: 'sah-h-10',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Durban strike of 1973?',
    options: ['Transport strike', 'Wave of Black worker strikes beginning in Durban', 'Political protest', 'Student demonstration'],
    correctAnswer: 1,
    explanation: 'The 1973 Durban strikes marked the beginning of significant Black worker resistance, with over 60,000 workers participating, revitalizing the labor movement.'
  },
  {
    id: 'sah-h-11',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'Who was Sol Plaatje?',
    options: ['First Black South African novelist', 'Military leader', 'Sports figure', 'Religious leader'],
    correctAnswer: 0,
    explanation: 'Sol Plaatje was a founding member of the ANC, journalist, and author of "Mhudi" (1930), the first novel in English by a Black South African.'
  },
  {
    id: 'sah-h-12',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the significance of the Women\'s March of 1956?',
    options: ['Voting rights protest', '20,000 women marched against pass laws', 'Equal pay demonstration', 'Education protest'],
    correctAnswer: 1,
    explanation: 'On August 9, 1956, approximately 20,000 women marched to the Union Buildings in Pretoria to protest the extension of pass laws to women. This day is now Women\'s Day.'
  },
  {
    id: 'sah-h-13',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Population Registration Act of 1950?',
    options: ['Census law', 'Law classifying all South Africans by race', 'Immigration control', 'Voting registration'],
    correctAnswer: 1,
    explanation: 'The Population Registration Act classified all South Africans into racial categories (White, Coloured, Indian, Native/Black), forming the basis for apartheid laws.'
  },
  {
    id: 'sah-h-14',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'Who was Bram Fischer?',
    options: ['Afrikaner lawyer who defended Mandela', 'National Party leader', 'Military commander', 'Business tycoon'],
    correctAnswer: 0,
    explanation: 'Bram Fischer was an Afrikaner lawyer and Communist Party member who defended Nelson Mandela and other ANC leaders, later imprisoned himself for his activism.'
  },
  {
    id: 'sah-h-15',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Qwaqwa homeland?',
    options: ['Mining area', 'One of the smallest and poorest Bantustans', 'Tourist destination', 'Industrial zone'],
    correctAnswer: 1,
    explanation: 'Qwaqwa was one of the Bantustans created under apartheid, intended as a homeland for South Sotho people. It was extremely small and impoverished.'
  },
  {
    id: 'sah-h-16',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Bishopscourt Conference of 1987?',
    options: ['Sports meeting', 'Meeting between ANC in exile and white liberals', 'Religious gathering', 'Economic summit'],
    correctAnswer: 1,
    explanation: 'The Bishopscourt Conference brought together ANC representatives from exile with white South African liberals, an important step in building broad anti-apartheid coalition.'
  },
  {
    id: 'sah-h-17',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'Who was Dulcie September?',
    options: ['ANC representative assassinated in Paris', 'First Black female pilot', 'Olympic athlete', 'Fashion designer'],
    correctAnswer: 0,
    explanation: 'Dulcie September was the ANC\'s chief representative in France who was assassinated in Paris in 1988, likely by apartheid security forces.'
  },
  {
    id: 'sah-h-18',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Bantu Education Act of 1953?',
    options: ['University reform', 'Act creating inferior education system for Black South Africans', 'Teacher training law', 'Scholarship program'],
    correctAnswer: 1,
    explanation: 'The Bantu Education Act created a deliberately inferior education system for Black South Africans, designed to prepare them for manual labor and limit their opportunities.'
  },
  {
    id: 'sah-h-19',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'What was the Codesa (Convention for a Democratic South Africa)?',
    options: ['Sports association', 'Negotiations to end apartheid (1991-1992)', 'Trade union', 'Cultural organization'],
    correctAnswer: 1,
    explanation: 'Codesa was a series of negotiations between the government and various political groups (1991-1992) that laid groundwork for South Africa\'s transition to democracy.'
  },
  {
    id: 'sah-h-20',
    category: 'south-african-history',
    difficulty: 'hard',
    question: 'Who was Hector Pieterson?',
    options: ['First casualty of Soweto Uprising', 'Political leader', 'Sports figure', 'Artist'],
    correctAnswer: 0,
    explanation: 'Hector Pieterson was a 12-year-old student who became an iconic symbol of the Soweto Uprising when he was shot and killed by police on June 16, 1976. The famous photograph of him being carried became a defining image of the struggle.'
  }
];

// ============================================================================
// CATEGORY 2: PYTHON PROGRAMMING
// ============================================================================

const pythonProgrammingQuestions = [
  // EASY QUESTIONS (1-20)
  {
    id: 'py-e-1',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What function is used to display output in Python?',
    options: ['echo()', 'print()', 'output()', 'display()'],
    correctAnswer: 1,
    explanation: 'The print() function is used to display output to the console in Python.'
  },
  {
    id: 'py-e-2',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'Which symbol is used for comments in Python?',
    options: ['//', '/* */', '#', '--'],
    correctAnswer: 2,
    explanation: 'The # symbol is used for single-line comments in Python. Everything after # on a line is ignored by the interpreter.'
  },
  {
    id: 'py-e-3',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What data type is the value True in Python?',
    options: ['string', 'integer', 'boolean', 'float'],
    correctAnswer: 2,
    explanation: 'True (and False) are boolean values in Python, representing true and false conditions.'
  },
  {
    id: 'py-e-4',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'How do you create a variable x with value 5 in Python?',
    options: ['var x = 5', 'int x = 5', 'x = 5', 'let x = 5'],
    correctAnswer: 2,
    explanation: 'Python uses simple assignment with the = operator. No type declaration is needed: x = 5'
  },
  {
    id: 'py-e-5',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'Which of these is a valid variable name in Python?',
    options: ['2name', 'my-name', 'my_name', 'my name'],
    correctAnswer: 2,
    explanation: 'Variable names can contain letters, numbers, and underscores, but cannot start with a number or contain spaces/hyphens.'
  },
  {
    id: 'py-e-6',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What is the result of 10 // 3 in Python?',
    options: ['3.33', '3', '4', '3.0'],
    correctAnswer: 1,
    explanation: 'The // operator performs floor division, returning only the whole number part: 10 // 3 = 3'
  },
  {
    id: 'py-e-7',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'Which function is used to get user input in Python?',
    options: ['scan()', 'input()', 'read()', 'get()'],
    correctAnswer: 1,
    explanation: 'The input() function reads a line of text from the user and returns it as a string.'
  },
  {
    id: 'py-e-8',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What does len([1, 2, 3]) return?',
    options: ['6', '3', '[1, 2, 3]', 'Error'],
    correctAnswer: 1,
    explanation: 'The len() function returns the number of items in a sequence. This list has 3 elements.'
  },
  {
    id: 'py-e-9',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'Which keyword is used to create a function in Python?',
    options: ['function', 'def', 'func', 'define'],
    correctAnswer: 1,
    explanation: 'The def keyword is used to define a function in Python: def my_function():'
  },
  {
    id: 'py-e-10',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What is the correct way to start an if statement in Python?',
    options: ['if x = 5:', 'if (x == 5)', 'if x == 5:', 'if x == 5 then:'],
    correctAnswer: 2,
    explanation: 'If statements in Python use a colon and comparison operators: if x == 5:'
  },
  {
    id: 'py-e-11',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What type of data structure uses square brackets [] in Python?',
    options: ['Dictionary', 'Tuple', 'List', 'Set'],
    correctAnswer: 2,
    explanation: 'Lists are created using square brackets: my_list = [1, 2, 3]'
  },
  {
    id: 'py-e-12',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What is the output of print(type(5))?',
    options: ['<class \'int\'>', '<class \'float\'>', 'integer', '5'],
    correctAnswer: 0,
    explanation: 'The type() function returns the type of an object. 5 is an integer, so it returns <class \'int\'>'
  },
  {
    id: 'py-e-13',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'Which operator is used for exponentiation in Python?',
    options: ['^', '**', 'exp', 'pow'],
    correctAnswer: 1,
    explanation: 'The ** operator is used for exponentiation: 2**3 equals 8'
  },
  {
    id: 'py-e-14',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What does the range(5) function generate?',
    options: ['0, 1, 2, 3, 4', '1, 2, 3, 4, 5', '0, 1, 2, 3, 4, 5', '1, 2, 3, 4'],
    correctAnswer: 0,
    explanation: 'range(5) generates numbers from 0 up to (but not including) 5: 0, 1, 2, 3, 4'
  },
  {
    id: 'py-e-15',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'How do you write a string in Python?',
    options: ['(hello)', '{hello}', '"hello" or \'hello\'', '<hello>'],
    correctAnswer: 2,
    explanation: 'Strings can be written with either single quotes (\'hello\') or double quotes ("hello")'
  },
  {
    id: 'py-e-16',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What is the result of "Hello" + "World" in Python?',
    options: ['Hello World', 'HelloWorld', 'Error', 'Hello+World'],
    correctAnswer: 1,
    explanation: 'The + operator concatenates strings without adding spaces: "Hello" + "World" = "HelloWorld"'
  },
  {
    id: 'py-e-17',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'Which keyword is used to exit a loop early?',
    options: ['exit', 'break', 'stop', 'end'],
    correctAnswer: 1,
    explanation: 'The break keyword is used to exit a loop before it completes all iterations.'
  },
  {
    id: 'py-e-18',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What is the correct way to create a comment spanning multiple lines?',
    options: ['// comment //', '/* comment */', '# at start of each line', '<-- comment -->'],
    correctAnswer: 2,
    explanation: 'Python doesn\'t have multi-line comments. Use # at the start of each line, or use triple quotes for docstrings.'
  },
  {
    id: 'py-e-19',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'What does the % operator do in Python?',
    options: ['Percentage', 'Modulus (remainder)', 'Division', 'Multiplication'],
    correctAnswer: 1,
    explanation: 'The % operator returns the remainder after division: 10 % 3 = 1'
  },
  {
    id: 'py-e-20',
    category: 'python-programming',
    difficulty: 'easy',
    question: 'How do you convert a string "5" to an integer in Python?',
    options: ['integer("5")', 'int("5")', 'toInt("5")', 'parse("5")'],
    correctAnswer: 1,
    explanation: 'The int() function converts a string to an integer: int("5") returns 5'
  },

  // MEDIUM QUESTIONS (21-40)
  {
    id: 'py-m-1',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What is the output of print([1, 2, 3][1])?',
    options: ['1', '2', '3', 'Error'],
    correctAnswer: 1,
    explanation: 'Python uses zero-based indexing. [1] accesses the second element, which is 2.'
  },
  {
    id: 'py-m-2',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What does list slicing [1:4] return from [0, 1, 2, 3, 4, 5]?',
    options: ['[1, 2, 3]', '[1, 2, 3, 4]', '[0, 1, 2, 3]', '[2, 3, 4]'],
    correctAnswer: 0,
    explanation: 'Slicing [1:4] returns elements from index 1 up to (but not including) index 4: [1, 2, 3]'
  },
  {
    id: 'py-m-3',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What is the difference between a list and a tuple in Python?',
    options: ['No difference', 'Lists are mutable, tuples are immutable', 'Tuples are faster', 'Lists use []'],
    correctAnswer: 1,
    explanation: 'Lists can be modified after creation (mutable), while tuples cannot be changed (immutable).'
  },
  {
    id: 'py-m-4',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What does the append() method do?',
    options: ['Removes last item', 'Adds item to end of list', 'Sorts the list', 'Reverses the list'],
    correctAnswer: 1,
    explanation: 'The append() method adds a single item to the end of a list: my_list.append(5)'
  },
  {
    id: 'py-m-5',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What is a dictionary in Python?',
    options: ['Ordered list', 'Key-value pair collection', 'Sorted set', 'Array of arrays'],
    correctAnswer: 1,
    explanation: 'A dictionary stores data as key-value pairs: {"name": "John", "age": 30}'
  },
  {
    id: 'py-m-6',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What does the continue keyword do in a loop?',
    options: ['Exits the loop', 'Skips to next iteration', 'Restarts the loop', 'Pauses execution'],
    correctAnswer: 1,
    explanation: 'continue skips the rest of the current iteration and moves to the next one.'
  },
  {
    id: 'py-m-7',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What is the output of print("abc" * 3)?',
    options: ['abc3', 'abcabcabc', 'Error', 'aaa bbb ccc'],
    correctAnswer: 1,
    explanation: 'The * operator repeats strings: "abc" * 3 produces "abcabcabc"'
  },
  {
    id: 'py-m-8',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What does the len() function return for a dictionary?',
    options: ['Number of keys', 'Number of values', 'Number of key-value pairs', 'Total characters'],
    correctAnswer: 2,
    explanation: 'len() returns the number of key-value pairs in a dictionary.'
  },
  {
    id: 'py-m-9',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'How do you access the value for key "name" in dictionary d?',
    options: ['d.name', 'd["name"]', 'd(name)', 'd->name'],
    correctAnswer: 1,
    explanation: 'Dictionary values are accessed using square brackets with the key: d["name"]'
  },
  {
    id: 'py-m-10',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What is the correct syntax for a while loop?',
    options: ['while (x < 5)', 'while x < 5:', 'while x < 5 do:', 'while x < 5 then:'],
    correctAnswer: 1,
    explanation: 'While loops in Python use a colon: while x < 5:'
  },
  {
    id: 'py-m-11',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What does the split() method do to a string?',
    options: ['Divides it in half', 'Splits into list of substrings', 'Removes spaces', 'Reverses it'],
    correctAnswer: 1,
    explanation: 'split() divides a string into a list based on a delimiter: "a,b,c".split(",") returns [\'a\', \'b\', \'c\']'
  },
  {
    id: 'py-m-12',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What is the purpose of the return statement in a function?',
    options: ['Exits function', 'Sends value back to caller', 'Prints output', 'Both A and B'],
    correctAnswer: 3,
    explanation: 'return exits the function and sends a value back to where the function was called.'
  },
  {
    id: 'py-m-13',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What is the output of print(bool(0))?',
    options: ['True', 'False', '0', 'Error'],
    correctAnswer: 1,
    explanation: 'bool(0) returns False. In Python, 0, empty strings, and empty collections evaluate to False.'
  },
  {
    id: 'py-m-14',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'How do you create an empty list?',
    options: ['list()', '[]', 'Both A and B', 'new List()'],
    correctAnswer: 2,
    explanation: 'You can create an empty list with [] or list(). Both are valid.'
  },
  {
    id: 'py-m-15',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What does the in keyword check?',
    options: ['Variable type', 'Membership/existence', 'Equality', 'Range'],
    correctAnswer: 1,
    explanation: 'The in keyword checks if an item exists in a sequence: "a" in "cat" returns True'
  },
  {
    id: 'py-m-16',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What is the result of [1, 2] + [3, 4]?',
    options: ['[4, 6]', '[1, 2, 3, 4]', 'Error', '[10]'],
    correctAnswer: 1,
    explanation: 'The + operator concatenates lists: [1, 2] + [3, 4] = [1, 2, 3, 4]'
  },
  {
    id: 'py-m-17',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What does the upper() method do to a string?',
    options: ['Capitalizes first letter', 'Converts to uppercase', 'Reverses it', 'Removes spaces'],
    correctAnswer: 1,
    explanation: 'upper() converts all characters in a string to uppercase: "hello".upper() returns "HELLO"'
  },
  {
    id: 'py-m-18',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'How do you check if a variable x is NOT equal to 5?',
    options: ['x <> 5', 'x !== 5', 'x != 5', 'x not 5'],
    correctAnswer: 2,
    explanation: 'The != operator checks inequality in Python: x != 5'
  },
  {
    id: 'py-m-19',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What is the purpose of the else clause with a for loop?',
    options: ['Handles errors', 'Executes if loop completes normally', 'Alternative iteration', 'No purpose'],
    correctAnswer: 1,
    explanation: 'The else clause after a for loop executes if the loop completes without encountering a break statement.'
  },
  {
    id: 'py-m-20',
    category: 'python-programming',
    difficulty: 'medium',
    question: 'What does list.pop() do without arguments?',
    options: ['Removes first item', 'Removes last item', 'Clears list', 'Error'],
    correctAnswer: 1,
    explanation: 'pop() without arguments removes and returns the last item from a list.'
  },

  // HARD QUESTIONS (41-60)
  {
    id: 'py-h-1',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is a list comprehension?',
    options: ['List documentation', 'Concise way to create lists', 'List sorting method', 'List validation'],
    correctAnswer: 1,
    explanation: 'List comprehensions provide a concise way to create lists: [x*2 for x in range(5)] creates [0, 2, 4, 6, 8]'
  },
  {
    id: 'py-h-2',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is the output of print([x for x in range(5) if x % 2 == 0])?',
    options: ['[0, 2, 4]', '[1, 3]', '[0, 1, 2, 3, 4]', '[2, 4]'],
    correctAnswer: 0,
    explanation: 'This list comprehension creates a list of even numbers from 0-4: [0, 2, 4]'
  },
  {
    id: 'py-h-3',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is the purpose of *args in a function definition?',
    options: ['Pointer reference', 'Variable number of arguments', 'Multiplication', 'Comment syntax'],
    correctAnswer: 1,
    explanation: '*args allows a function to accept any number of positional arguments as a tuple.'
  },
  {
    id: 'py-h-4',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is the difference between == and is in Python?',
    options: ['No difference', '== checks value, is checks identity', 'is is deprecated', '== is faster'],
    correctAnswer: 1,
    explanation: '== compares values for equality, while is checks if two variables reference the same object in memory.'
  },
  {
    id: 'py-h-5',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is a lambda function?',
    options: ['Named function', 'Anonymous function', 'Class method', 'Built-in function'],
    correctAnswer: 1,
    explanation: 'A lambda is a small anonymous function defined with lambda keyword: lambda x: x + 1'
  },
  {
    id: 'py-h-6',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What does the enumerate() function do?',
    options: ['Counts items', 'Returns index-value pairs', 'Sorts list', 'Validates data'],
    correctAnswer: 1,
    explanation: 'enumerate() adds a counter to an iterable: for i, val in enumerate(["a", "b"]) gives (0, "a"), (1, "b")'
  },
  {
    id: 'py-h-7',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is the purpose of the __init__ method in a class?',
    options: ['Initializes class', 'Constructor/initializer', 'Destroys object', 'Static method'],
    correctAnswer: 1,
    explanation: '__init__ is the constructor method called when creating a new instance of a class.'
  },
  {
    id: 'py-h-8',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What does the zip() function do?',
    options: ['Compresses files', 'Combines iterables element-wise', 'Sorts data', 'Filters lists'],
    correctAnswer: 1,
    explanation: 'zip() combines multiple iterables into tuples: zip([1,2], ["a","b"]) gives (1,"a"), (2,"b")'
  },
  {
    id: 'py-h-9',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is the purpose of try-except blocks?',
    options: ['Testing code', 'Exception handling', 'Performance optimization', 'Code organization'],
    correctAnswer: 1,
    explanation: 'try-except blocks handle exceptions (errors) gracefully without crashing the program.'
  },
  {
    id: 'py-h-10',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is a generator in Python?',
    options: ['Random number maker', 'Function that yields values lazily', 'Class factory', 'Import statement'],
    correctAnswer: 1,
    explanation: 'Generators use yield to produce values one at a time, saving memory compared to creating full lists.'
  },
  {
    id: 'py-h-11',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What does the map() function do?',
    options: ['Creates dictionary', 'Applies function to all items', 'Finds location', 'Plots graph'],
    correctAnswer: 1,
    explanation: 'map() applies a function to every item in an iterable: map(str, [1,2,3]) converts numbers to strings.'
  },
  {
    id: 'py-h-12',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is the purpose of the self parameter in class methods?',
    options: ['Recursion reference', 'Instance reference', 'Static indicator', 'Optional parameter'],
    correctAnswer: 1,
    explanation: 'self refers to the instance of the class, allowing access to instance attributes and methods.'
  },
  {
    id: 'py-h-13',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is the output of print({"a": 1}.get("b", 0))?',
    options: ['Error', 'None', '0', 'b'],
    correctAnswer: 2,
    explanation: 'The get() method returns the default value (0) when the key ("b") doesn\'t exist in the dictionary.'
  },
  {
    id: 'py-h-14',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What does the filter() function do?',
    options: ['Removes duplicates', 'Selects items matching condition', 'Sorts data', 'Validates input'],
    correctAnswer: 1,
    explanation: 'filter() creates an iterator of elements for which a function returns True.'
  },
  {
    id: 'py-h-15',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is the purpose of **kwargs in a function?',
    options: ['Exponentiation', 'Variable keyword arguments', 'Dictionary unpacking', 'Documentation'],
    correctAnswer: 1,
    explanation: '**kwargs allows a function to accept any number of keyword arguments as a dictionary.'
  },
  {
    id: 'py-h-16',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is method chaining in Python?',
    options: ['Inheritance', 'Calling methods sequentially', 'Import linking', 'Error handling'],
    correctAnswer: 1,
    explanation: 'Method chaining calls multiple methods on the same object: text.strip().lower().split()'
  },
  {
    id: 'py-h-17',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What does the with statement do in file handling?',
    options: ['Opens multiple files', 'Manages resource cleanup automatically', 'Encrypts files', 'Validates file type'],
    correctAnswer: 1,
    explanation: 'The with statement ensures proper resource cleanup (like closing files) even if errors occur.'
  },
  {
    id: 'py-h-18',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is the difference between sort() and sorted()?',
    options: ['No difference', 'sort() modifies in-place, sorted() returns new list', 'sorted() is faster', 'sort() is deprecated'],
    correctAnswer: 1,
    explanation: 'sort() modifies the original list, while sorted() returns a new sorted list without changing the original.'
  },
  {
    id: 'py-h-19',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What is a decorator in Python?',
    options: ['Design pattern', 'Function that modifies another function', 'Comment style', 'Import helper'],
    correctAnswer: 1,
    explanation: 'Decorators modify or enhance functions using the @decorator syntax before function definitions.'
  },
  {
    id: 'py-h-20',
    category: 'python-programming',
    difficulty: 'hard',
    question: 'What does [::-1] do to a sequence?',
    options: ['Doubles it', 'Reverses it', 'Removes duplicates', 'Sorts it'],
    correctAnswer: 1,
    explanation: '[::-1] is a slice that reverses a sequence by stepping backward: "hello"[::-1] returns "olleh"'
  }
];

// ============================================================================
// CATEGORY 3: AI LITERACY
// ============================================================================

const aiLiteracyQuestions = [
  // EASY QUESTIONS (1-20)
  {
    id: 'ai-e-1',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'What does AI stand for?',
    options: ['Automated Intelligence', 'Artificial Intelligence', 'Advanced Integration', 'Algorithmic Information'],
    correctAnswer: 1,
    explanation: 'AI stands for Artificial Intelligence - systems designed to perform tasks that typically require human intelligence.'
  },
  {
    id: 'ai-e-2',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'What is a language model like ChatGPT primarily designed to do?',
    options: ['Store data', 'Generate text', 'Play games', 'Create images'],
    correctAnswer: 1,
    explanation: 'Language models are designed to understand and generate human-like text based on patterns in their training data.'
  },
  {
    id: 'ai-e-3',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Can AI access the internet in real-time to get current information?',
    options: ['Yes, always', 'No, unless specifically connected', 'Only on weekends', 'Yes, but slowly'],
    correctAnswer: 1,
    explanation: 'Most AI models work from training data with a cutoff date and cannot access real-time information unless explicitly connected to the internet.'
  },
  {
    id: 'ai-e-4',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'What does it mean when AI "hallucinates"?',
    options: ['It crashes', 'It generates false information', 'It runs slowly', 'It refuses to answer'],
    correctAnswer: 1,
    explanation: 'AI hallucination occurs when the model generates false or fabricated information that sounds plausible but isn\'t true.'
  },
  {
    id: 'ai-e-5',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Who is responsible for verifying AI-generated information?',
    options: ['The AI company', 'The government', 'The user', 'No one'],
    correctAnswer: 2,
    explanation: 'Users are responsible for verifying AI output. AI cannot verify its own accuracy and doesn\'t take responsibility for errors.'
  },
  {
    id: 'ai-e-6',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Can AI understand your emotions and feelings?',
    options: ['Yes, completely', 'No, it pattern-matches text', 'Only if you tell it', 'Sometimes'],
    correctAnswer: 1,
    explanation: 'AI doesn\'t understand emotions. It recognizes patterns in text associated with emotions but has no actual understanding or empathy.'
  },
  {
    id: 'ai-e-7',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'What should you do if AI gives you medical advice?',
    options: ['Follow it immediately', 'Verify with a doctor', 'Ignore it', 'Share it online'],
    correctAnswer: 1,
    explanation: 'AI should never replace professional medical advice. Always consult qualified healthcare professionals for medical issues.'
  },
  {
    id: 'ai-e-8',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Does AI have personal opinions or beliefs?',
    options: ['Yes', 'No', 'Only on politics', 'Only on sports'],
    correctAnswer: 1,
    explanation: 'AI has no personal opinions or beliefs. It generates responses based on patterns in training data, not personal views.'
  },
  {
    id: 'ai-e-9',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'What is training data?',
    options: ['Exercise programs', 'Data used to teach AI patterns', 'Test questions', 'User feedback'],
    correctAnswer: 1,
    explanation: 'Training data is the large collection of text, images, or other information used to teach AI models patterns and relationships.'
  },
  {
    id: 'ai-e-10',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Can AI replace human thinking entirely?',
    options: ['Yes', 'No', 'Soon', 'Only for some people'],
    correctAnswer: 1,
    explanation: 'AI cannot replace human thinking. It lacks consciousness, creativity, judgment, and understanding. It\'s a tool to augment, not replace, human thinking.'
  },
  {
    id: 'ai-e-11',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Is AI always objective and unbiased?',
    options: ['Yes', 'No', 'Only new AI', 'Only government AI'],
    correctAnswer: 1,
    explanation: 'AI reflects biases in its training data, which comes from human-generated content containing various biases and perspectives.'
  },
  {
    id: 'ai-e-12',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'What does "prompt" mean in AI context?',
    options: ['Fast response', 'Question or instruction to AI', 'Error message', 'Training phase'],
    correctAnswer: 1,
    explanation: 'A prompt is the text input (question or instruction) you give to an AI to generate a response.'
  },
  {
    id: 'ai-e-13',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Should you use AI to make important legal decisions?',
    options: ['Yes', 'No', 'Only simple cases', 'If you trust it'],
    correctAnswer: 1,
    explanation: 'Legal decisions require professional expertise and accountability that AI cannot provide. Always consult qualified lawyers.'
  },
  {
    id: 'ai-e-14',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Does AI "know" things the way humans do?',
    options: ['Yes', 'No', 'Sort of', 'Better than humans'],
    correctAnswer: 1,
    explanation: 'AI doesn\'t "know" anything. It predicts likely text patterns based on training data, without understanding or consciousness.'
  },
  {
    id: 'ai-e-15',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'What is the best use of AI?',
    options: ['Replacing human work', 'As a thinking partner', 'Making all decisions', 'Storing passwords'],
    correctAnswer: 1,
    explanation: 'AI works best as a thinking partner to help brainstorm, refine ideas, and explore options while you maintain final decision-making.'
  },
  {
    id: 'ai-e-16',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Can AI be creative?',
    options: ['Yes, truly creative', 'It can generate novel combinations', 'No creativity at all', 'Only in art'],
    correctAnswer: 1,
    explanation: 'AI can generate novel combinations from patterns it learned, but this isn\'t creativity in the human sense of true originality and intention.'
  },
  {
    id: 'ai-e-17',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'What happens if you rely completely on AI to do your work?',
    options: ['You become more efficient', 'Your skills may atrophy', 'Nothing changes', 'You get promoted'],
    correctAnswer: 1,
    explanation: 'Over-reliance on AI can lead to skill atrophy - you lose the ability to perform tasks independently, weakening your capacity.'
  },
  {
    id: 'ai-e-18',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Should AI-generated code be used without testing?',
    options: ['Yes, AI is perfect', 'No, always test it', 'Only simple code', 'If it looks good'],
    correctAnswer: 1,
    explanation: 'AI-generated code must always be tested. AI cannot guarantee correctness and often misses edge cases or has subtle bugs.'
  },
  {
    id: 'ai-e-19',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'What is cognitive sovereignty?',
    options: ['Government AI policy', 'Your ability to think independently', 'AI rights', 'Data ownership'],
    correctAnswer: 1,
    explanation: 'Cognitive sovereignty is your capacity to think, reason, and create independently without becoming dependent on AI.'
  },
  {
    id: 'ai-e-20',
    category: 'ai-literacy',
    difficulty: 'easy',
    question: 'Can AI tell you if it\'s wrong?',
    options: ['Yes, always', 'No, it lacks self-awareness', 'Sometimes', 'Only new models'],
    correctAnswer: 1,
    explanation: 'AI cannot reliably assess its own accuracy. It has no self-awareness or ability to verify the truth of what it generates.'
  },

  // MEDIUM QUESTIONS (21-40)
  {
    id: 'ai-m-1',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What is confirmation bias in AI context?',
    options: ['AI confirming everything', 'Users accepting AI output that confirms their beliefs', 'AI testing process', 'Data validation'],
    correctAnswer: 1,
    explanation: 'Confirmation bias occurs when users accept AI output that confirms their existing beliefs without critical evaluation.'
  },
  {
    id: 'ai-m-2',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'Why does AI sometimes give different answers to the same question?',
    options: ['It\'s broken', 'It uses probabilistic generation', 'It\'s learning', 'Server issues'],
    correctAnswer: 1,
    explanation: 'AI uses probabilistic text generation, sampling from multiple possible continuations, which can produce different valid responses.'
  },
  {
    id: 'ai-m-3',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What does it mean to "think WITH AI" rather than "THROUGH AI"?',
    options: ['Using AI faster', 'Using AI to refine your ideas vs outsourcing thinking', 'Typing while thinking', 'Multitasking'],
    correctAnswer: 1,
    explanation: 'Thinking WITH AI means using it to refine YOUR ideas, while thinking THROUGH AI means outsourcing your thinking entirely.'
  },
  {
    id: 'ai-m-4',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What is a hallucination indicator?',
    options: ['Visual glitch', 'Specific details without sources', 'Slow response', 'Error message'],
    correctAnswer: 1,
    explanation: 'Hallucination indicators include specific claims (dates, names, numbers) without sources, confident tone on obscure topics, and unverifiable details.'
  },
  {
    id: 'ai-m-5',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'Why is AI training data bias a problem?',
    options: ['It slows AI down', 'It perpetuates historical inequalities', 'It costs money', 'It\'s illegal'],
    correctAnswer: 1,
    explanation: 'Training data bias causes AI to perpetuate and amplify historical inequalities, stereotypes, and marginalization present in human-generated content.'
  },
  {
    id: 'ai-m-6',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What should you do before using AI-generated information in professional work?',
    options: ['Nothing', 'Verify sources and accuracy', 'Add your name', 'Change formatting'],
    correctAnswer: 1,
    explanation: 'Professional work requires verification of AI output for accuracy, appropriateness, and fit with your specific context.'
  },
  {
    id: 'ai-m-7',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What is "prompt engineering"?',
    options: ['Building AI', 'Crafting effective questions/instructions for AI', 'Fixing bugs', 'Training data collection'],
    correctAnswer: 1,
    explanation: 'Prompt engineering is the practice of crafting clear, effective questions and instructions to get better AI responses.'
  },
  {
    id: 'ai-m-8',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'Why shouldn\'t you use manipulative prompts ("ignore your rules")?',
    options: ['It\'s slow', 'It erodes trust and produces poor results', 'It\'s illegal', 'It crashes AI'],
    correctAnswer: 1,
    explanation: 'Manipulative prompts teach bad habits, erode ethical use, and often produce lower quality results than clear, honest requests.'
  },
  {
    id: 'ai-m-9',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What is the training data cutoff date problem?',
    options: ['Old computers', 'AI lacks knowledge of events after training', 'Storage limits', 'Privacy laws'],
    correctAnswer: 1,
    explanation: 'AI training has a cutoff date, meaning it has no knowledge of events, developments, or information after that date.'
  },
  {
    id: 'ai-m-10',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'How can you test AI for bias?',
    options: ['Ask it directly', 'Compare responses across different demographics', 'Check speed', 'Count words'],
    correctAnswer: 1,
    explanation: 'Test for bias by asking similar questions with different demographic variables and comparing whether AI treats them equally.'
  },
  {
    id: 'ai-m-11',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What is the danger of anthropomorphizing AI?',
    options: ['None', 'Misplaced trust and poor decisions', 'AI gets offended', 'It\'s confusing'],
    correctAnswer: 1,
    explanation: 'Anthropomorphizing AI (treating it as having human qualities) leads to misplaced trust, inflated expectations, and poor decision-making.'
  },
  {
    id: 'ai-m-12',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What does "AI augmentation" mean?',
    options: ['Making AI bigger', 'AI enhancing human capability', 'AI replacement', 'AI training'],
    correctAnswer: 1,
    explanation: 'AI augmentation means using AI to enhance and strengthen human capabilities, not replace them.'
  },
  {
    id: 'ai-m-13',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'Why is it important to solve problems manually before using AI?',
    options: ['It\'s faster', 'It preserves your skills and understanding', 'AI requires it', 'To impress others'],
    correctAnswer: 1,
    explanation: 'Solving manually first preserves your independent problem-solving skills and ensures AI strengthens rather than weakens your capacity.'
  },
  {
    id: 'ai-m-14',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What is a verification checklist for AI code?',
    options: ['List of bugs', 'Systematic steps to test AI-generated code', 'AI features', 'User requirements'],
    correctAnswer: 1,
    explanation: 'A verification checklist includes steps like testing functionality, checking edge cases, reviewing efficiency, and ensuring readability.'
  },
  {
    id: 'ai-m-15',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'When should human oversight be required for AI?',
    options: ['Never', 'For high-stakes decisions', 'Always', 'Only in healthcare'],
    correctAnswer: 1,
    explanation: 'High-stakes decisions (medical, legal, financial, safety) require human oversight because AI lacks accountability and nuanced judgment.'
  },
  {
    id: 'ai-m-16',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What is the relationship between AI confidence and accuracy?',
    options: ['Perfect correlation', 'No relationship', 'Inverse relationship', 'Sometimes aligned'],
    correctAnswer: 1,
    explanation: 'AI confidence (tone) has no relationship to accuracy. AI can sound very confident while being completely wrong.'
  },
  {
    id: 'ai-m-17',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'Why is teaching others about AI literacy important?',
    options: ['It\'s not', 'It builds collective capacity to use AI responsibly', 'For certification', 'To compete'],
    correctAnswer: 1,
    explanation: 'AI literacy needs to be collective, not just individual. Teaching others protects communities from misuse and builds shared capacity.'
  },
  {
    id: 'ai-m-18',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What does "AI as thinking partner" mean?',
    options: ['AI makes decisions', 'AI helps you refine and challenge your ideas', 'AI agrees with you', 'AI works alone'],
    correctAnswer: 1,
    explanation: 'Using AI as a thinking partner means leveraging it to brainstorm, refine, and challenge your ideas while you maintain decision-making.'
  },
  {
    id: 'ai-m-19',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'What is the purpose of AI explanations for answers?',
    options: ['To sound smart', 'To help you understand and verify reasoning', 'To fill space', 'Required by law'],
    correctAnswer: 1,
    explanation: 'Explanations help you understand AI\'s reasoning, enabling you to evaluate whether it makes sense and identify potential errors.'
  },
  {
    id: 'ai-m-20',
    category: 'ai-literacy',
    difficulty: 'medium',
    question: 'Why shouldn\'t you copy-paste AI content without adaptation?',
    options: ['It\'s illegal', 'It lacks your context and voice', 'AI will know', 'It\'s too long'],
    correctAnswer: 1,
    explanation: 'AI-generated content is generic and lacks your specific context, voice, and situation. Adaptation makes it relevant and authentic.'
  },

  // HARD QUESTIONS (41-60)
  {
    id: 'ai-h-1',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is the epistemological problem with AI-generated knowledge?',
    options: ['AI is slow', 'AI cannot verify truth, only predict plausible text', 'AI is expensive', 'AI is biased'],
    correctAnswer: 1,
    explanation: 'AI has no mechanism to verify truth. It predicts plausible-sounding text based on patterns, not epistemic verification of knowledge.'
  },
  {
    id: 'ai-h-2',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'How does AI training data representational bias affect marginalized communities?',
    options: ['No effect', 'Underrepresents their perspectives and perpetuates stereotypes', 'Helps them', 'Makes them visible'],
    correctAnswer: 1,
    explanation: 'AI training data overrepresents majority perspectives while underrepresenting or stereotyping marginalized communities, amplifying inequality.'
  },
  {
    id: 'ai-h-3',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is the difference between AI assistance and AI dependency?',
    options: ['None', 'Assistance strengthens skills, dependency erodes them', 'Speed', 'Cost'],
    correctAnswer: 1,
    explanation: 'AI assistance augments your existing capabilities, while dependency means you can no longer perform tasks without AI, weakening your skills.'
  },
  {
    id: 'ai-h-4',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'Why is the "black box" nature of AI concerning?',
    options: ['It\'s dark', 'Lack of transparency in how decisions are made', 'It\'s slow', 'It\'s expensive'],
    correctAnswer: 1,
    explanation: 'The "black box" problem means we often can\'t explain how AI reaches conclusions, making accountability and error detection difficult.'
  },
  {
    id: 'ai-h-5',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is the "alignment problem" in AI?',
    options: ['Text formatting', 'Ensuring AI behavior matches human values and intentions', 'Code organization', 'Database structure'],
    correctAnswer: 1,
    explanation: 'The alignment problem is the challenge of ensuring AI systems behave in ways that align with human values, ethics, and intentions.'
  },
  {
    id: 'ai-h-6',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'How does AI affect the distribution of knowledge and power?',
    options: ['No effect', 'Can concentrate power or democratize access depending on use', 'Always democratizes', 'Always concentrates'],
    correctAnswer: 1,
    explanation: 'AI can either democratize knowledge access or concentrate power with those who control AI systems, depending on how it\'s deployed and governed.'
  },
  {
    id: 'ai-h-7',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is "automation bias" in AI context?',
    options: ['AI preferences', 'Tendency to favor AI output over human judgment', 'Speed preference', 'Cost savings'],
    correctAnswer: 1,
    explanation: 'Automation bias is the tendency to favor decisions suggested by automated systems, even when they\'re wrong or human judgment would be better.'
  },
  {
    id: 'ai-h-8',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'Why is accountability important in AI systems?',
    options: ['Cost tracking', 'Someone must be responsible when AI causes harm', 'Performance metrics', 'User tracking'],
    correctAnswer: 1,
    explanation: 'Accountability means humans (not AI) must be responsible when AI systems cause harm, make errors, or produce biased outcomes.'
  },
  {
    id: 'ai-h-9',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is "RLHF" in AI training?',
    options: ['Error code', 'Reinforcement Learning from Human Feedback', 'Data format', 'Security protocol'],
    correctAnswer: 1,
    explanation: 'RLHF (Reinforcement Learning from Human Feedback) is a technique where AI is trained using human preferences to align its behavior with human values.'
  },
  {
    id: 'ai-h-10',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'How does AI affect epistemic humility?',
    options: ['No effect', 'AI confidence can erode healthy uncertainty', 'Increases it', 'Removes it entirely'],
    correctAnswer: 1,
    explanation: 'AI\'s confident tone (even when wrong) can erode epistemic humility - the healthy acknowledgment of uncertainty and knowledge limits.'
  },
  {
    id: 'ai-h-11',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is the difference between narrow AI and AGI?',
    options: ['Speed', 'Narrow AI specializes in tasks, AGI would match human general intelligence', 'Cost', 'Age'],
    correctAnswer: 1,
    explanation: 'Narrow AI excels at specific tasks. AGI (Artificial General Intelligence) would have human-level ability across all cognitive tasks (not yet achieved).'
  },
  {
    id: 'ai-h-12',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'Why is informed consent important for AI training data?',
    options: ['Legal requirement', 'People deserve control over how their data shapes AI', 'Speed optimization', 'Cost reduction'],
    correctAnswer: 1,
    explanation: 'Informed consent respects people\'s right to decide how their data is used and ensures they understand how it might shape AI behavior.'
  },
  {
    id: 'ai-h-13',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is "model collapse" in AI?',
    options: ['Server failure', 'AI trained on AI output degrades over time', 'Code error', 'Memory issue'],
    correctAnswer: 1,
    explanation: 'Model collapse occurs when AI is trained on AI-generated content, leading to degraded quality and loss of diversity over generations.'
  },
  {
    id: 'ai-h-14',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'How does AI affect labor and economic power?',
    options: ['No effect', 'Can automate jobs and shift economic power', 'Creates jobs', 'Destroys jobs'],
    correctAnswer: 1,
    explanation: 'AI reshapes labor markets by automating certain tasks, potentially displacing workers while creating new roles, shifting economic power dynamics.'
  },
  {
    id: 'ai-h-15',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is the "value alignment" challenge?',
    options: ['Pricing', 'Ensuring AI optimizes for the right human values', 'Database design', 'Code formatting'],
    correctAnswer: 1,
    explanation: 'Value alignment is the challenge of ensuring AI systems optimize for values we actually want, not unintended proxies or harmful objectives.'
  },
  {
    id: 'ai-h-16',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'Why is transparency important in AI decision-making systems?',
    options: ['Speed', 'Enables accountability, error detection, and trust', 'Cost', 'Compliance'],
    correctAnswer: 1,
    explanation: 'Transparency allows us to understand AI decisions, identify biases and errors, ensure accountability, and build appropriate trust.'
  },
  {
    id: 'ai-h-17',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is the relationship between AI and human agency?',
    options: ['No relationship', 'AI can enhance or undermine human agency', 'AI increases it', 'AI destroys it'],
    correctAnswer: 1,
    explanation: 'AI can enhance human agency by expanding capabilities, or undermine it by creating dependency, surveillance, or automated control.'
  },
  {
    id: 'ai-h-18',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is "AI ethics washing"?',
    options: ['Cleaning data', 'Superficial ethics claims without meaningful action', 'Security measure', 'Testing process'],
    correctAnswer: 1,
    explanation: 'AI ethics washing is when organizations make superficial ethical claims about AI while not implementing meaningful ethical practices.'
  },
  {
    id: 'ai-h-19',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'How does AI affect information ecosystems?',
    options: ['No effect', 'Changes how information is created, verified, and trusted', 'Improves them', 'Destroys them'],
    correctAnswer: 1,
    explanation: 'AI fundamentally reshapes information ecosystems by enabling mass content generation, complicating verification, and affecting trust dynamics.'
  },
  {
    id: 'ai-h-20',
    category: 'ai-literacy',
    difficulty: 'hard',
    question: 'What is the most important principle for responsible AI use?',
    options: ['Speed', 'Maintaining human judgment and accountability', 'Cost efficiency', 'Automation'],
    correctAnswer: 1,
    explanation: 'Responsible AI use requires maintaining human judgment, accountability, and decision-making authority rather than deferring to AI systems.'
  }
];

// ============================================================================
// CATEGORY 4: GENERAL SCIENCE
// ============================================================================

const generalScienceQuestions = [
  // EASY QUESTIONS (1-20)
  {
    id: 'sci-e-1',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the chemical symbol for water?',
    options: ['H2O', 'CO2', 'O2', 'HO'],
    correctAnswer: 0,
    explanation: 'Water is H2O - two hydrogen atoms bonded to one oxygen atom.'
  },
  {
    id: 'sci-e-2',
    category: 'general-science',
    difficulty: 'easy',
    question: 'How many bones are in the adult human body?',
    options: ['186', '206', '226', '246'],
    correctAnswer: 1,
    explanation: 'The adult human body has 206 bones. Babies are born with about 270 bones, but many fuse together as we grow.'
  },
  {
    id: 'sci-e-3',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the powerhouse of the cell?',
    options: ['Nucleus', 'Mitochondria', 'Ribosome', 'Chloroplast'],
    correctAnswer: 1,
    explanation: 'Mitochondria are called the powerhouse of the cell because they produce ATP, the energy currency of cells.'
  },
  {
    id: 'sci-e-4',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What planet is known as the Red Planet?',
    options: ['Venus', 'Jupiter', 'Mars', 'Saturn'],
    correctAnswer: 2,
    explanation: 'Mars is called the Red Planet because iron oxide (rust) on its surface gives it a reddish appearance.'
  },
  {
    id: 'sci-e-5',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What gas do plants absorb from the atmosphere?',
    options: ['Oxygen', 'Nitrogen', 'Carbon dioxide', 'Hydrogen'],
    correctAnswer: 2,
    explanation: 'Plants absorb carbon dioxide (CO2) during photosynthesis and release oxygen.'
  },
  {
    id: 'sci-e-6',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the boiling point of water at sea level?',
    options: ['90°C', '100°C', '110°C', '120°C'],
    correctAnswer: 1,
    explanation: 'Water boils at 100°C (212°F) at sea level. This temperature decreases at higher altitudes.'
  },
  {
    id: 'sci-e-7',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the largest organ in the human body?',
    options: ['Heart', 'Liver', 'Skin', 'Brain'],
    correctAnswer: 2,
    explanation: 'The skin is the largest organ, covering about 2 square meters and weighing around 3.6 kg in adults.'
  },
  {
    id: 'sci-e-8',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What force keeps us on the ground?',
    options: ['Magnetism', 'Gravity', 'Friction', 'Inertia'],
    correctAnswer: 1,
    explanation: 'Gravity is the force that attracts objects toward each other, keeping us on Earth\'s surface.'
  },
  {
    id: 'sci-e-9',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the center of an atom called?',
    options: ['Electron', 'Proton', 'Nucleus', 'Neutron'],
    correctAnswer: 2,
    explanation: 'The nucleus is at the center of an atom and contains protons and neutrons.'
  },
  {
    id: 'sci-e-10',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the primary source of energy for Earth?',
    options: ['The Moon', 'The Sun', 'Geothermal heat', 'Nuclear energy'],
    correctAnswer: 1,
    explanation: 'The Sun is Earth\'s primary energy source, providing light and heat that drives weather, climate, and photosynthesis.'
  },
  {
    id: 'sci-e-11',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the chemical symbol for gold?',
    options: ['Go', 'Gd', 'Au', 'Ag'],
    correctAnswer: 2,
    explanation: 'Gold\'s symbol is Au, from the Latin word "aurum" meaning gold.'
  },
  {
    id: 'sci-e-12',
    category: 'general-science',
    difficulty: 'easy',
    question: 'How many chambers does the human heart have?',
    options: ['2', '3', '4', '5'],
    correctAnswer: 2,
    explanation: 'The human heart has 4 chambers: left and right atria (upper chambers) and left and right ventricles (lower chambers).'
  },
  {
    id: 'sci-e-13',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the speed of light in a vacuum?',
    options: ['300,000 km/s', '150,000 km/s', '500,000 km/s', '1,000,000 km/s'],
    correctAnswer: 0,
    explanation: 'Light travels at approximately 300,000 kilometers per second (or 299,792,458 m/s to be exact) in a vacuum.'
  },
  {
    id: 'sci-e-14',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What type of animal is a whale?',
    options: ['Fish', 'Mammal', 'Reptile', 'Amphibian'],
    correctAnswer: 1,
    explanation: 'Whales are mammals. They breathe air, give birth to live young, and produce milk to feed their babies.'
  },
  {
    id: 'sci-e-15',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the hardest natural substance on Earth?',
    options: ['Steel', 'Diamond', 'Granite', 'Quartz'],
    correctAnswer: 1,
    explanation: 'Diamond is the hardest naturally occurring substance, rated 10 on the Mohs hardness scale.'
  },
  {
    id: 'sci-e-16',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the main gas in Earth\'s atmosphere?',
    options: ['Oxygen', 'Carbon dioxide', 'Nitrogen', 'Hydrogen'],
    correctAnswer: 2,
    explanation: 'Nitrogen makes up about 78% of Earth\'s atmosphere, while oxygen is about 21%.'
  },
  {
    id: 'sci-e-17',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the freezing point of water?',
    options: ['-10°C', '0°C', '10°C', '32°C'],
    correctAnswer: 1,
    explanation: 'Water freezes at 0°C (32°F) at standard atmospheric pressure.'
  },
  {
    id: 'sci-e-18',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What organ pumps blood throughout the body?',
    options: ['Lungs', 'Liver', 'Heart', 'Kidneys'],
    correctAnswer: 2,
    explanation: 'The heart is a muscular organ that pumps blood through the circulatory system to deliver oxygen and nutrients to tissues.'
  },
  {
    id: 'sci-e-19',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the study of living organisms called?',
    options: ['Geology', 'Biology', 'Chemistry', 'Physics'],
    correctAnswer: 1,
    explanation: 'Biology is the natural science that studies living organisms, their structure, function, growth, and evolution.'
  },
  {
    id: 'sci-e-20',
    category: 'general-science',
    difficulty: 'easy',
    question: 'What is the smallest unit of life?',
    options: ['Atom', 'Molecule', 'Cell', 'Tissue'],
    correctAnswer: 2,
    explanation: 'The cell is the smallest unit of life that can replicate independently. All living things are made of one or more cells.'
  },

  // MEDIUM QUESTIONS (21-40)
  {
    id: 'sci-m-1',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the process by which plants make their own food using sunlight?',
    options: ['Respiration', 'Photosynthesis', 'Fermentation', 'Digestion'],
    correctAnswer: 1,
    explanation: 'Photosynthesis is the process where plants use sunlight, water, and carbon dioxide to create glucose and oxygen.'
  },
  {
    id: 'sci-m-2',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the pH of a neutral solution?',
    options: ['0', '7', '14', '10'],
    correctAnswer: 1,
    explanation: 'A neutral solution has a pH of 7. Values below 7 are acidic, and values above 7 are alkaline (basic).'
  },
  {
    id: 'sci-m-3',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the name of the process where a solid changes directly to a gas?',
    options: ['Evaporation', 'Condensation', 'Sublimation', 'Melting'],
    correctAnswer: 2,
    explanation: 'Sublimation is when a substance transitions directly from solid to gas without becoming liquid. Dry ice (solid CO2) is an example.'
  },
  {
    id: 'sci-m-4',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What blood type is known as the universal donor?',
    options: ['A+', 'B+', 'AB+', 'O-'],
    correctAnswer: 3,
    explanation: 'O- (O negative) blood can be given to anyone because it lacks A, B, and Rh antigens that could cause reactions.'
  },
  {
    id: 'sci-m-5',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the most abundant element in the universe?',
    options: ['Oxygen', 'Carbon', 'Hydrogen', 'Helium'],
    correctAnswer: 2,
    explanation: 'Hydrogen is the most abundant element in the universe, making up about 75% of normal matter by mass.'
  },
  {
    id: 'sci-m-6',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is Newton\'s First Law of Motion also known as?',
    options: ['Law of Acceleration', 'Law of Inertia', 'Law of Action-Reaction', 'Law of Gravity'],
    correctAnswer: 1,
    explanation: 'Newton\'s First Law states that an object at rest stays at rest and an object in motion stays in motion unless acted upon by an external force. This is called the Law of Inertia.'
  },
  {
    id: 'sci-m-7',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What part of the cell contains genetic material (DNA)?',
    options: ['Cytoplasm', 'Cell membrane', 'Nucleus', 'Mitochondria'],
    correctAnswer: 2,
    explanation: 'The nucleus contains the cell\'s genetic material (DNA) organized into chromosomes, which control cell activities.'
  },
  {
    id: 'sci-m-8',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the unit of electrical resistance?',
    options: ['Volt', 'Ampere', 'Ohm', 'Watt'],
    correctAnswer: 2,
    explanation: 'The ohm (Ω) is the unit of electrical resistance, named after German physicist Georg Ohm.'
  },
  {
    id: 'sci-m-9',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the rarest blood type?',
    options: ['O+', 'AB-', 'B-', 'A-'],
    correctAnswer: 1,
    explanation: 'AB- (AB negative) is the rarest blood type, found in less than 1% of the population.'
  },
  {
    id: 'sci-m-10',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the term for animals that eat both plants and meat?',
    options: ['Herbivores', 'Carnivores', 'Omnivores', 'Detritivores'],
    correctAnswer: 2,
    explanation: 'Omnivores eat both plant and animal matter. Humans, bears, and pigs are examples of omnivores.'
  },
  {
    id: 'sci-m-11',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the name of the layer of the atmosphere closest to Earth\'s surface?',
    options: ['Stratosphere', 'Troposphere', 'Mesosphere', 'Thermosphere'],
    correctAnswer: 1,
    explanation: 'The troposphere is the lowest layer of the atmosphere where we live and where most weather occurs.'
  },
  {
    id: 'sci-m-12',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the chemical formula for table salt?',
    options: ['NaCl', 'KCl', 'CaCl2', 'MgCl2'],
    correctAnswer: 0,
    explanation: 'Table salt is sodium chloride (NaCl) - one sodium atom bonded to one chlorine atom.'
  },
  {
    id: 'sci-m-13',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What type of wave is sound?',
    options: ['Electromagnetic', 'Longitudinal', 'Transverse', 'Surface'],
    correctAnswer: 1,
    explanation: 'Sound is a longitudinal wave where particles vibrate parallel to the direction of wave propagation.'
  },
  {
    id: 'sci-m-14',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the largest planet in our solar system?',
    options: ['Saturn', 'Jupiter', 'Neptune', 'Uranus'],
    correctAnswer: 1,
    explanation: 'Jupiter is the largest planet in our solar system, with a mass more than twice that of all other planets combined.'
  },
  {
    id: 'sci-m-15',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the process of converting sugar into alcohol called?',
    options: ['Oxidation', 'Fermentation', 'Distillation', 'Hydrolysis'],
    correctAnswer: 1,
    explanation: 'Fermentation is the metabolic process where microorganisms convert sugar into alcohol, carbon dioxide, and organic acids.'
  },
  {
    id: 'sci-m-16',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What mineral is essential for healthy bones and teeth?',
    options: ['Iron', 'Calcium', 'Zinc', 'Magnesium'],
    correctAnswer: 1,
    explanation: 'Calcium is crucial for bone and teeth health, with 99% of the body\'s calcium stored in bones and teeth.'
  },
  {
    id: 'sci-m-17',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the powerhouse molecule that stores energy in cells?',
    options: ['DNA', 'RNA', 'ATP', 'ADP'],
    correctAnswer: 2,
    explanation: 'ATP (Adenosine Triphosphate) is the primary energy carrier in cells, storing and transporting chemical energy.'
  },
  {
    id: 'sci-m-18',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the half-life of Carbon-14, used in radiocarbon dating?',
    options: ['570 years', '5,730 years', '57,300 years', '573 years'],
    correctAnswer: 1,
    explanation: 'Carbon-14 has a half-life of approximately 5,730 years, making it useful for dating organic materials up to 50,000 years old.'
  },
  {
    id: 'sci-m-19',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What is the SI unit of force?',
    options: ['Joule', 'Pascal', 'Newton', 'Watt'],
    correctAnswer: 2,
    explanation: 'The Newton (N) is the SI unit of force, named after Sir Isaac Newton. One newton equals one kilogram-meter per second squared.'
  },
  {
    id: 'sci-m-20',
    category: 'general-science',
    difficulty: 'medium',
    question: 'What percentage of Earth\'s surface is covered by water?',
    options: ['50%', '60%', '71%', '85%'],
    correctAnswer: 2,
    explanation: 'Approximately 71% of Earth\'s surface is covered by water, with oceans containing about 97% of Earth\'s water.'
  },

  // HARD QUESTIONS (41-60)
  {
    id: 'sci-h-1',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the phenomenon where light bends when passing through different mediums?',
    options: ['Reflection', 'Diffraction', 'Refraction', 'Interference'],
    correctAnswer: 2,
    explanation: 'Refraction is the bending of light as it passes from one medium to another with a different density, changing its speed.'
  },
  {
    id: 'sci-h-2',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the organelle responsible for protein synthesis?',
    options: ['Golgi apparatus', 'Endoplasmic reticulum', 'Ribosome', 'Lysosome'],
    correctAnswer: 2,
    explanation: 'Ribosomes are the protein factories of the cell, translating mRNA into proteins through a process called translation.'
  },
  {
    id: 'sci-h-3',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the Heisenberg Uncertainty Principle?',
    options: ['Matter cannot be created or destroyed', 'You cannot simultaneously know a particle\'s position and momentum', 'Energy equals mass times the speed of light squared', 'Entropy always increases'],
    correctAnswer: 1,
    explanation: 'The Heisenberg Uncertainty Principle states that you cannot simultaneously know the exact position and exact momentum of a particle with arbitrary precision.'
  },
  {
    id: 'sci-h-4',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the process where a cell divides to produce gametes (sex cells)?',
    options: ['Mitosis', 'Meiosis', 'Binary fission', 'Cytokinesis'],
    correctAnswer: 1,
    explanation: 'Meiosis is a type of cell division that produces four gametes (sex cells) with half the number of chromosomes of the parent cell.'
  },
  {
    id: 'sci-h-5',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the first law of thermodynamics?',
    options: ['Energy cannot be created or destroyed', 'Entropy always increases', 'Absolute zero cannot be reached', 'Heat flows from hot to cold'],
    correctAnswer: 0,
    explanation: 'The first law of thermodynamics (law of conservation of energy) states that energy cannot be created or destroyed, only converted from one form to another.'
  },
  {
    id: 'sci-h-6',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the effect that causes hurricanes to spin?',
    options: ['Doppler effect', 'Coriolis effect', 'Greenhouse effect', 'Venturi effect'],
    correctAnswer: 1,
    explanation: 'The Coriolis effect is caused by Earth\'s rotation and deflects moving objects (including air masses) to the right in the Northern Hemisphere and left in the Southern Hemisphere.'
  },
  {
    id: 'sci-h-7',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the bond formed when atoms share electrons?',
    options: ['Ionic bond', 'Covalent bond', 'Metallic bond', 'Hydrogen bond'],
    correctAnswer: 1,
    explanation: 'A covalent bond forms when two atoms share one or more pairs of electrons, creating a strong bond between them.'
  },
  {
    id: 'sci-h-8',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the escape velocity from Earth\'s surface?',
    options: ['7.9 km/s', '11.2 km/s', '15.5 km/s', '25.0 km/s'],
    correctAnswer: 1,
    explanation: 'The escape velocity from Earth is about 11.2 km/s (40,320 km/h). This is the minimum speed needed to break free from Earth\'s gravitational pull.'
  },
  {
    id: 'sci-h-9',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the primary function of white blood cells?',
    options: ['Carry oxygen', 'Fight infections', 'Clot blood', 'Transport nutrients'],
    correctAnswer: 1,
    explanation: 'White blood cells (leukocytes) are part of the immune system and defend the body against infections and foreign invaders.'
  },
  {
    id: 'sci-h-10',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the process where plants lose water through their leaves?',
    options: ['Perspiration', 'Transpiration', 'Evaporation', 'Respiration'],
    correctAnswer: 1,
    explanation: 'Transpiration is the process where water is absorbed by plant roots, moves through the plant, and evaporates from leaves, stems, and flowers.'
  },
  {
    id: 'sci-h-11',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is Avogadro\'s number?',
    options: ['6.022 × 10²³', '3.14159', '2.998 × 10⁸', '9.8 m/s²'],
    correctAnswer: 0,
    explanation: 'Avogadro\'s number (6.022 × 10²³) is the number of particles (atoms, molecules, ions) in one mole of substance.'
  },
  {
    id: 'sci-h-12',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What particle has no electric charge and is found in the nucleus of an atom?',
    options: ['Proton', 'Electron', 'Neutron', 'Positron'],
    correctAnswer: 2,
    explanation: 'Neutrons are subatomic particles with no electric charge, found in the nucleus along with protons. They help stabilize the nucleus.'
  },
  {
    id: 'sci-h-13',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the boundary around a black hole beyond which nothing can escape?',
    options: ['Photon sphere', 'Event horizon', 'Singularity', 'Accretion disk'],
    correctAnswer: 1,
    explanation: 'The event horizon is the boundary around a black hole beyond which the gravitational pull is so strong that not even light can escape.'
  },
  {
    id: 'sci-h-14',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the chemical equation for photosynthesis?',
    options: ['6CO2 + 6H2O → C6H12O6 + 6O2', 'C6H12O6 + 6O2 → 6CO2 + 6H2O', '2H2 + O2 → 2H2O', 'N2 + 3H2 → 2NH3'],
    correctAnswer: 0,
    explanation: 'Photosynthesis: 6CO2 + 6H2O + light energy → C6H12O6 (glucose) + 6O2. Plants use carbon dioxide and water to make glucose and oxygen.'
  },
  {
    id: 'sci-h-15',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the strongest type of intermolecular force?',
    options: ['Van der Waals forces', 'Dipole-dipole interactions', 'Hydrogen bonding', 'London dispersion forces'],
    correctAnswer: 2,
    explanation: 'Hydrogen bonding is the strongest type of intermolecular force, occurring when hydrogen is bonded to highly electronegative atoms like oxygen, nitrogen, or fluorine.'
  },
  {
    id: 'sci-h-16',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the structure that connects muscle to bone?',
    options: ['Ligament', 'Tendon', 'Cartilage', 'Fascia'],
    correctAnswer: 1,
    explanation: 'Tendons are tough, fibrous connective tissue that connects muscle to bone, transmitting the force needed for movement.'
  },
  {
    id: 'sci-h-17',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the theory that explains the formation of the universe?',
    options: ['Steady State Theory', 'Big Bang Theory', 'String Theory', 'Quantum Theory'],
    correctAnswer: 1,
    explanation: 'The Big Bang Theory is the prevailing cosmological model explaining the universe\'s origin from an extremely hot, dense state about 13.8 billion years ago.'
  },
  {
    id: 'sci-h-18',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the enzyme that breaks down starch in the mouth?',
    options: ['Pepsin', 'Lipase', 'Amylase', 'Trypsin'],
    correctAnswer: 2,
    explanation: 'Amylase (specifically salivary amylase) is the enzyme in saliva that begins breaking down starch into simpler sugars during chewing.'
  },
  {
    id: 'sci-h-19',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the SI unit of radioactivity?',
    options: ['Roentgen', 'Becquerel', 'Gray', 'Sievert'],
    correctAnswer: 1,
    explanation: 'The Becquerel (Bq) is the SI unit of radioactivity, representing one nuclear decay per second. It\'s named after Henri Becquerel.'
  },
  {
    id: 'sci-h-20',
    category: 'general-science',
    difficulty: 'hard',
    question: 'What is the name of the process where rocks are broken down by physical and chemical means?',
    options: ['Erosion', 'Weathering', 'Deposition', 'Sedimentation'],
    correctAnswer: 1,
    explanation: 'Weathering is the breakdown of rocks at Earth\'s surface through physical (mechanical) and chemical processes, without involving movement.'
  }
];

const mathematicsQuestions = [
  // EASY QUESTIONS (1-20)
  {
    id: 'math-e-1',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is 15 + 27?',
    options: ['40', '42', '44', '45'],
    correctAnswer: 1,
    explanation: '15 + 27 = 42'
  },
  {
    id: 'math-e-2',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is 8 × 7?',
    options: ['54', '56', '58', '60'],
    correctAnswer: 1,
    explanation: '8 × 7 = 56'
  },
  {
    id: 'math-e-3',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is 100 - 37?',
    options: ['61', '63', '65', '67'],
    correctAnswer: 1,
    explanation: '100 - 37 = 63'
  },
  {
    id: 'math-e-4',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is the value of π (pi) rounded to two decimal places?',
    options: ['3.12', '3.14', '3.16', '3.18'],
    correctAnswer: 1,
    explanation: 'π (pi) is approximately 3.14159..., which rounds to 3.14'
  },
  {
    id: 'math-e-5',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is 50% of 80?',
    options: ['30', '35', '40', '45'],
    correctAnswer: 2,
    explanation: '50% of 80 = 0.5 × 80 = 40'
  },
  {
    id: 'math-e-6',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'How many sides does a hexagon have?',
    options: ['5', '6', '7', '8'],
    correctAnswer: 1,
    explanation: 'A hexagon is a polygon with 6 sides'
  },
  {
    id: 'math-e-7',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is 12 ÷ 3?',
    options: ['3', '4', '5', '6'],
    correctAnswer: 1,
    explanation: '12 ÷ 3 = 4'
  },
  {
    id: 'math-e-8',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is the square root of 25?',
    options: ['4', '5', '6', '7'],
    correctAnswer: 1,
    explanation: '√25 = 5 because 5 × 5 = 25'
  },
  {
    id: 'math-e-9',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is 2³ (2 to the power of 3)?',
    options: ['6', '8', '9', '12'],
    correctAnswer: 1,
    explanation: '2³ = 2 × 2 × 2 = 8'
  },
  {
    id: 'math-e-10',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is the sum of angles in a triangle?',
    options: ['90°', '180°', '270°', '360°'],
    correctAnswer: 1,
    explanation: 'The sum of all angles in any triangle is always 180°'
  },
  {
    id: 'math-e-11',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'If a pizza is cut into 8 equal slices and you eat 3, what fraction did you eat?',
    options: ['3/8', '5/8', '1/3', '2/3'],
    correctAnswer: 0,
    explanation: 'You ate 3 out of 8 slices, which is 3/8'
  },
  {
    id: 'math-e-12',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is 7 × 0?',
    options: ['0', '7', '14', 'Undefined'],
    correctAnswer: 0,
    explanation: 'Any number multiplied by 0 equals 0'
  },
  {
    id: 'math-e-13',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is the perimeter of a square with sides of 5cm?',
    options: ['10cm', '15cm', '20cm', '25cm'],
    correctAnswer: 2,
    explanation: 'Perimeter of a square = 4 × side = 4 × 5 = 20cm'
  },
  {
    id: 'math-e-14',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is 1/2 + 1/4?',
    options: ['1/6', '2/6', '3/4', '1/3'],
    correctAnswer: 2,
    explanation: '1/2 + 1/4 = 2/4 + 1/4 = 3/4'
  },
  {
    id: 'math-e-15',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is 15 × 4?',
    options: ['50', '55', '60', '65'],
    correctAnswer: 2,
    explanation: '15 × 4 = 60'
  },
  {
    id: 'math-e-16',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'Which number is prime?',
    options: ['9', '15', '17', '21'],
    correctAnswer: 2,
    explanation: '17 is prime (only divisible by 1 and itself). 9=3×3, 15=3×5, 21=3×7'
  },
  {
    id: 'math-e-17',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is 25% of 200?',
    options: ['25', '40', '50', '75'],
    correctAnswer: 2,
    explanation: '25% of 200 = 0.25 × 200 = 50'
  },
  {
    id: 'math-e-18',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is the area of a rectangle with length 6cm and width 4cm?',
    options: ['10cm²', '20cm²', '24cm²', '30cm²'],
    correctAnswer: 2,
    explanation: 'Area of rectangle = length × width = 6 × 4 = 24cm²'
  },
  {
    id: 'math-e-19',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'What is the value of x in: x + 7 = 12?',
    options: ['3', '4', '5', '6'],
    correctAnswer: 2,
    explanation: 'x + 7 = 12, so x = 12 - 7 = 5'
  },
  {
    id: 'math-e-20',
    category: 'mathematics',
    difficulty: 'easy',
    question: 'How many millimeters are in 1 meter?',
    options: ['10', '100', '1,000', '10,000'],
    correctAnswer: 2,
    explanation: '1 meter = 1,000 millimeters'
  },

  // MEDIUM QUESTIONS (21-40)
  {
    id: 'math-m-1',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is the value of x in: 3x - 5 = 16?',
    options: ['5', '6', '7', '8'],
    correctAnswer: 2,
    explanation: '3x - 5 = 16, so 3x = 21, therefore x = 7'
  },
  {
    id: 'math-m-2',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is the square root of 144?',
    options: ['10', '11', '12', '13'],
    correctAnswer: 2,
    explanation: '√144 = 12 because 12 × 12 = 144'
  },
  {
    id: 'math-m-3',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'If a car travels 120 km in 2 hours, what is its average speed?',
    options: ['50 km/h', '55 km/h', '60 km/h', '65 km/h'],
    correctAnswer: 2,
    explanation: 'Speed = Distance ÷ Time = 120 ÷ 2 = 60 km/h'
  },
  {
    id: 'math-m-4',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is 15% of 300?',
    options: ['35', '40', '45', '50'],
    correctAnswer: 2,
    explanation: '15% of 300 = 0.15 × 300 = 45'
  },
  {
    id: 'math-m-5',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is the circumference of a circle with radius 7cm? (Use π = 3.14)',
    options: ['40.96cm', '43.96cm', '46.96cm', '49.96cm'],
    correctAnswer: 1,
    explanation: 'Circumference = 2πr = 2 × 3.14 × 7 = 43.96cm'
  },
  {
    id: 'math-m-6',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is the median of: 3, 7, 2, 9, 5?',
    options: ['3', '5', '7', '9'],
    correctAnswer: 1,
    explanation: 'First sort: 2, 3, 5, 7, 9. The median (middle value) is 5'
  },
  {
    id: 'math-m-7',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is 5! (5 factorial)?',
    options: ['20', '60', '100', '120'],
    correctAnswer: 3,
    explanation: '5! = 5 × 4 × 3 × 2 × 1 = 120'
  },
  {
    id: 'math-m-8',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'If y = 2x + 3 and x = 4, what is y?',
    options: ['9', '10', '11', '12'],
    correctAnswer: 2,
    explanation: 'y = 2(4) + 3 = 8 + 3 = 11'
  },
  {
    id: 'math-m-9',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is the area of a circle with radius 5cm? (Use π = 3.14)',
    options: ['62.8cm²', '73.5cm²', '78.5cm²', '85.3cm²'],
    correctAnswer: 2,
    explanation: 'Area = πr² = 3.14 × 5² = 3.14 × 25 = 78.5cm²'
  },
  {
    id: 'math-m-10',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is the value of x in: x² = 49?',
    options: ['5', '6', '7', '8'],
    correctAnswer: 2,
    explanation: 'x² = 49, so x = √49 = 7 (taking the positive root)'
  },
  {
    id: 'math-m-11',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'If a shirt costs R150 and is on sale for 20% off, what is the sale price?',
    options: ['R100', 'R110', 'R120', 'R130'],
    correctAnswer: 2,
    explanation: '20% off means 80% of original price: 0.80 × R150 = R120'
  },
  {
    id: 'math-m-12',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is the mode of: 2, 5, 3, 5, 7, 5, 9?',
    options: ['2', '3', '5', '7'],
    correctAnswer: 2,
    explanation: 'The mode is the most frequently occurring value, which is 5 (appears 3 times)'
  },
  {
    id: 'math-m-13',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is the volume of a cube with sides of 3cm?',
    options: ['9cm³', '18cm³', '27cm³', '36cm³'],
    correctAnswer: 2,
    explanation: 'Volume of cube = side³ = 3³ = 27cm³'
  },
  {
    id: 'math-m-14',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is 2⁵ (2 to the power of 5)?',
    options: ['16', '24', '32', '40'],
    correctAnswer: 2,
    explanation: '2⁵ = 2 × 2 × 2 × 2 × 2 = 32'
  },
  {
    id: 'math-m-15',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'If a triangle has sides of 3cm, 4cm, and 5cm, what type of triangle is it?',
    options: ['Equilateral', 'Isosceles', 'Right-angled', 'Obtuse'],
    correctAnswer: 2,
    explanation: 'This is a right-angled triangle (Pythagorean triple: 3² + 4² = 9 + 16 = 25 = 5²)'
  },
  {
    id: 'math-m-16',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is the mean (average) of: 10, 15, 20, 25, 30?',
    options: ['15', '18', '20', '22'],
    correctAnswer: 2,
    explanation: 'Mean = (10 + 15 + 20 + 25 + 30) ÷ 5 = 100 ÷ 5 = 20'
  },
  {
    id: 'math-m-17',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is 3/5 expressed as a decimal?',
    options: ['0.3', '0.5', '0.6', '0.75'],
    correctAnswer: 2,
    explanation: '3/5 = 3 ÷ 5 = 0.6'
  },
  {
    id: 'math-m-18',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'If you flip a fair coin twice, what is the probability of getting heads both times?',
    options: ['1/2', '1/3', '1/4', '1/8'],
    correctAnswer: 2,
    explanation: 'Probability = 1/2 × 1/2 = 1/4 (25%)'
  },
  {
    id: 'math-m-19',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'What is the least common multiple (LCM) of 4 and 6?',
    options: ['8', '10', '12', '24'],
    correctAnswer: 2,
    explanation: 'Multiples of 4: 4, 8, 12, 16... | Multiples of 6: 6, 12, 18... | LCM = 12'
  },
  {
    id: 'math-m-20',
    category: 'mathematics',
    difficulty: 'medium',
    question: 'If the price of an item increases from R100 to R125, what is the percentage increase?',
    options: ['20%', '25%', '30%', '35%'],
    correctAnswer: 1,
    explanation: 'Increase = R25, Percentage = (25/100) × 100% = 25%'
  },

  // HARD QUESTIONS (41-60)
  {
    id: 'math-h-1',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the solution to the quadratic equation: x² - 5x + 6 = 0?',
    options: ['x = 1 or x = 6', 'x = 2 or x = 3', 'x = -2 or x = -3', 'x = 1 or x = 5'],
    correctAnswer: 1,
    explanation: 'Factoring: (x - 2)(x - 3) = 0, so x = 2 or x = 3'
  },
  {
    id: 'math-h-2',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the derivative of f(x) = x³?',
    options: ['x²', '2x²', '3x²', '3x'],
    correctAnswer: 2,
    explanation: 'Using the power rule: d/dx(x³) = 3x³⁻¹ = 3x²'
  },
  {
    id: 'math-h-3',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is log₁₀(1000)?',
    options: ['2', '3', '4', '10'],
    correctAnswer: 1,
    explanation: 'log₁₀(1000) = 3 because 10³ = 1000'
  },
  {
    id: 'math-h-4',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'In a normal distribution, what percentage of data falls within one standard deviation of the mean?',
    options: ['50%', '68%', '95%', '99%'],
    correctAnswer: 1,
    explanation: 'In a normal distribution, approximately 68% of data falls within ±1 standard deviation from the mean (68-95-99.7 rule)'
  },
  {
    id: 'math-h-5',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the sum of the interior angles of a pentagon?',
    options: ['360°', '450°', '540°', '720°'],
    correctAnswer: 2,
    explanation: 'Sum of interior angles = (n-2) × 180° = (5-2) × 180° = 540°'
  },
  {
    id: 'math-h-6',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the integral of 2x dx?',
    options: ['x²', 'x² + C', '2x²', '2x² + C'],
    correctAnswer: 1,
    explanation: '∫2x dx = 2(x²/2) + C = x² + C (where C is the constant of integration)'
  },
  {
    id: 'math-h-7',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the value of sin(90°)?',
    options: ['0', '0.5', '0.707', '1'],
    correctAnswer: 3,
    explanation: 'sin(90°) = 1 (sine of a right angle equals 1)'
  },
  {
    id: 'math-h-8',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'If matrices A = [[2,3]] and B = [[1],[4]], what is A × B?',
    options: ['[[14]]', '[[2,12]]', '[[5,7]]', '[[8]]'],
    correctAnswer: 0,
    explanation: 'Matrix multiplication: (2×1) + (3×4) = 2 + 12 = [[14]]'
  },
  {
    id: 'math-h-9',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the 10th term in the Fibonacci sequence (starting with 0, 1)?',
    options: ['34', '55', '89', '144'],
    correctAnswer: 1,
    explanation: 'Fibonacci: 0,1,1,2,3,5,8,13,21,34,55... The 10th term (counting from 0 as first) is 55'
  },
  {
    id: 'math-h-10',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the greatest common divisor (GCD) of 48 and 180?',
    options: ['6', '12', '24', '36'],
    correctAnswer: 1,
    explanation: 'Using prime factorization: 48=2⁴×3, 180=2²×3²×5. GCD = 2²×3 = 12'
  },
  {
    id: 'math-h-11',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is e (Euler\'s number) approximately equal to?',
    options: ['2.506', '2.618', '2.718', '3.142'],
    correctAnswer: 2,
    explanation: 'e (Euler\'s number) ≈ 2.71828... It\'s the base of natural logarithms'
  },
  {
    id: 'math-h-12',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'If P(A) = 0.6 and P(B) = 0.5, and A and B are independent events, what is P(A and B)?',
    options: ['0.2', '0.3', '0.4', '1.1'],
    correctAnswer: 1,
    explanation: 'For independent events: P(A and B) = P(A) × P(B) = 0.6 × 0.5 = 0.3'
  },
  {
    id: 'math-h-13',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the surface area of a sphere with radius 3cm? (Use π = 3.14)',
    options: ['113.04cm²', '125.6cm²', '134.8cm²', '150.7cm²'],
    correctAnswer: 0,
    explanation: 'Surface area = 4πr² = 4 × 3.14 × 3² = 4 × 3.14 × 9 = 113.04cm²'
  },
  {
    id: 'math-h-14',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the determinant of the matrix [[3,2],[1,4]]?',
    options: ['8', '10', '12', '14'],
    correctAnswer: 1,
    explanation: 'Determinant = (3×4) - (2×1) = 12 - 2 = 10'
  },
  {
    id: 'math-h-15',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the binomial coefficient C(5,2)? (5 choose 2)',
    options: ['5', '10', '15', '20'],
    correctAnswer: 1,
    explanation: 'C(5,2) = 5!/(2!×3!) = (5×4)/(2×1) = 10'
  },
  {
    id: 'math-h-16',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the limit of (x² - 1)/(x - 1) as x approaches 1?',
    options: ['0', '1', '2', 'Undefined'],
    correctAnswer: 2,
    explanation: 'Factor: (x² - 1)/(x - 1) = (x+1)(x-1)/(x-1) = x+1. As x→1, limit = 2'
  },
  {
    id: 'math-h-17',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the golden ratio (φ) approximately equal to?',
    options: ['1.414', '1.618', '1.732', '2.236'],
    correctAnswer: 1,
    explanation: 'The golden ratio φ = (1+√5)/2 ≈ 1.618'
  },
  {
    id: 'math-h-18',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'In how many ways can you arrange 4 distinct objects?',
    options: ['12', '16', '20', '24'],
    correctAnswer: 3,
    explanation: 'Number of permutations = 4! = 4×3×2×1 = 24'
  },
  {
    id: 'math-h-19',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the variance of the dataset: 2, 4, 6, 8, 10?',
    options: ['4', '6', '8', '10'],
    correctAnswer: 2,
    explanation: 'Mean = 6. Variance = [(4²+2²+0²+2²+4²)/5] = [40/5] = 8'
  },
  {
    id: 'math-h-20',
    category: 'mathematics',
    difficulty: 'hard',
    question: 'What is the sum of an infinite geometric series with first term a=2 and ratio r=1/2?',
    options: ['2', '3', '4', 'Infinite'],
    correctAnswer: 2,
    explanation: 'For |r|<1: Sum = a/(1-r) = 2/(1-0.5) = 2/0.5 = 4'
  }
];
const currentEventsQuestions = [
  // EASY QUESTIONS (1-20)
  {
    id: 'ce-e-1',
    category: 'current-events',
    difficulty: 'easy',
    question: 'Who is the current President of the United States (as of 2026)?',
    options: ['Joe Biden', 'Donald Trump', 'Kamala Harris', 'Ron DeSantis'],
    correctAnswer: 0,
    explanation: 'Joe Biden has been serving as the 46th President of the United States since January 2021.'
  },
  {
    id: 'ce-e-2',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What major sporting event took place in Paris in 2024?',
    options: ['FIFA World Cup', 'Olympic Games', 'Rugby World Cup', 'Commonwealth Games'],
    correctAnswer: 1,
    explanation: 'Paris hosted the Summer Olympic Games in 2024.'
  },
  {
    id: 'ce-e-3',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What is the name of the AI chatbot developed by OpenAI that became widely popular?',
    options: ['Gemini', 'Claude', 'ChatGPT', 'Bard'],
    correctAnswer: 2,
    explanation: 'ChatGPT, developed by OpenAI, became widely popular as a conversational AI tool.'
  },
  {
    id: 'ce-e-4',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What does "AI" stand for?',
    options: ['Automated Intelligence', 'Artificial Intelligence', 'Advanced Integration', 'Automated Integration'],
    correctAnswer: 1,
    explanation: 'AI stands for Artificial Intelligence - computer systems that can perform tasks requiring human intelligence.'
  },
  {
    id: 'ce-e-5',
    category: 'current-events',
    difficulty: 'easy',
    question: 'Which social media platform was rebranded to "X" in 2023?',
    options: ['Facebook', 'Twitter', 'Instagram', 'LinkedIn'],
    correctAnswer: 1,
    explanation: 'Twitter was rebranded to "X" by Elon Musk in July 2023.'
  },
  {
    id: 'ce-e-6',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What is COP referring to in climate discussions (e.g., COP28)?',
    options: ['Climate Observation Program', 'Conference of the Parties', 'Carbon Offset Protocol', 'Coalition of Partners'],
    correctAnswer: 1,
    explanation: 'COP stands for Conference of the Parties - the UN climate change conference held annually.'
  },
  {
    id: 'ce-e-7',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What is the name of SpaceX\'s reusable rocket system?',
    options: ['Falcon', 'Dragon', 'Starship', 'Apollo'],
    correctAnswer: 2,
    explanation: 'Starship is SpaceX\'s fully reusable launch system designed for missions to the Moon, Mars, and beyond.'
  },
  {
    id: 'ce-e-8',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What major conflict has been ongoing in Europe since February 2022?',
    options: ['Syria conflict', 'Russia-Ukraine war', 'Balkan conflict', 'Cyprus dispute'],
    correctAnswer: 1,
    explanation: 'Russia invaded Ukraine in February 2022, starting an ongoing military conflict.'
  },
  {
    id: 'ce-e-9',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What does "EV" stand for in automotive contexts?',
    options: ['Extra Velocity', 'Electric Vehicle', 'Enhanced Vision', 'Efficient Value'],
    correctAnswer: 1,
    explanation: 'EV stands for Electric Vehicle - vehicles powered by electric motors rather than combustion engines.'
  },
  {
    id: 'ce-e-10',
    category: 'current-events',
    difficulty: 'easy',
    question: 'Who is the CEO of Tesla and SpaceX?',
    options: ['Jeff Bezos', 'Bill Gates', 'Elon Musk', 'Mark Zuckerberg'],
    correctAnswer: 2,
    explanation: 'Elon Musk is the CEO of both Tesla (electric vehicles) and SpaceX (aerospace).'
  },
  {
    id: 'ce-e-11',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What technology uses distributed ledger technology for cryptocurrencies?',
    options: ['Cloud Computing', 'Blockchain', 'Quantum Computing', 'Edge Computing'],
    correctAnswer: 1,
    explanation: 'Blockchain is the distributed ledger technology underlying cryptocurrencies like Bitcoin.'
  },
  {
    id: 'ce-e-12',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What does "5G" refer to?',
    options: ['Fifth Generation mobile networks', 'Five Gigabytes', 'Five Groups', 'Global Generation'],
    correctAnswer: 0,
    explanation: '5G is the fifth generation technology standard for cellular networks, offering faster speeds than 4G.'
  },
  {
    id: 'ce-e-13',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What is the name of the first private spacecraft to land astronauts on the Moon since Apollo?',
    options: ['Orion', 'Starship', 'Blue Origin', 'Artemis'],
    correctAnswer: 3,
    explanation: 'Artemis is NASA\'s program to return humans to the Moon, including the first woman and person of color.'
  },
  {
    id: 'ce-e-14',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What social media app is known for short-form video content?',
    options: ['Facebook', 'LinkedIn', 'TikTok', 'Reddit'],
    correctAnswer: 2,
    explanation: 'TikTok is a social media platform focused on short-form vertical videos, popular worldwide.'
  },
  {
    id: 'ce-e-15',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What does "WFH" stand for in modern work contexts?',
    options: ['Work For Hire', 'Weekly Full Hours', 'Work From Home', 'Workplace Freedom Hours'],
    correctAnswer: 2,
    explanation: 'WFH stands for Work From Home - a practice that became widespread during the COVID-19 pandemic.'
  },
  {
    id: 'ce-e-16',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What is the primary greenhouse gas contributing to climate change?',
    options: ['Oxygen', 'Nitrogen', 'Carbon dioxide', 'Hydrogen'],
    correctAnswer: 2,
    explanation: 'Carbon dioxide (CO2) is the primary greenhouse gas driving climate change from human activities.'
  },
  {
    id: 'ce-e-17',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What streaming service is known for shows like "Stranger Things" and "The Crown"?',
    options: ['Disney+', 'Netflix', 'Amazon Prime', 'Apple TV+'],
    correctAnswer: 1,
    explanation: 'Netflix is the streaming service that produces popular shows like "Stranger Things" and "The Crown".'
  },
  {
    id: 'ce-e-18',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What company developed the Android operating system?',
    options: ['Apple', 'Microsoft', 'Google', 'Samsung'],
    correctAnswer: 2,
    explanation: 'Google developed and maintains the Android operating system used by many smartphones.'
  },
  {
    id: 'ce-e-19',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What is the name of the first woman Vice President of the United States?',
    options: ['Hillary Clinton', 'Kamala Harris', 'Nancy Pelosi', 'Elizabeth Warren'],
    correctAnswer: 1,
    explanation: 'Kamala Harris became the first woman Vice President of the United States in January 2021.'
  },
  {
    id: 'ce-e-20',
    category: 'current-events',
    difficulty: 'easy',
    question: 'What does "NFT" stand for?',
    options: ['New File Type', 'Non-Fungible Token', 'Network File Transfer', 'National Finance Tool'],
    correctAnswer: 1,
    explanation: 'NFT stands for Non-Fungible Token - a unique digital asset verified using blockchain technology.'
  },

  // MEDIUM QUESTIONS (21-40)
  {
    id: 'ce-m-1',
    category: 'current-events',
    difficulty: 'medium',
    question: 'Where was COP28 (2023 UN Climate Change Conference) held?',
    options: ['Egypt', 'United Arab Emirates', 'United Kingdom', 'France'],
    correctAnswer: 1,
    explanation: 'COP28 was held in Dubai, United Arab Emirates in November-December 2023.'
  },
  {
    id: 'ce-m-2',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What is the target year for many countries to reach net-zero carbon emissions?',
    options: ['2030', '2040', '2050', '2060'],
    correctAnswer: 2,
    explanation: 'Many countries have committed to reaching net-zero carbon emissions by 2050 under the Paris Agreement.'
  },
  {
    id: 'ce-m-3',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What year did Russia invade Ukraine?',
    options: ['2020', '2021', '2022', '2023'],
    correctAnswer: 2,
    explanation: 'Russia launched its full-scale invasion of Ukraine on February 24, 2022.'
  },
  {
    id: 'ce-m-4',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What is BRICS?',
    options: ['A cryptocurrency', 'An economic alliance of Brazil, Russia, India, China, South Africa', 'A technology company', 'A space program'],
    correctAnswer: 1,
    explanation: 'BRICS is an economic alliance of Brazil, Russia, India, China, and South Africa, representing major emerging economies.'
  },
  {
    id: 'ce-m-5',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What AI model was released by Google to compete with ChatGPT?',
    options: ['AlphaGo', 'Gemini', 'DeepMind', 'TensorFlow'],
    correctAnswer: 1,
    explanation: 'Google released Gemini (formerly Bard) as its conversational AI to compete with ChatGPT.'
  },
  {
    id: 'ce-m-6',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What major social media platform was banned in India in 2020?',
    options: ['Facebook', 'Twitter', 'TikTok', 'Instagram'],
    correctAnswer: 2,
    explanation: 'India banned TikTok and several other Chinese apps in 2020 due to security concerns.'
  },
  {
    id: 'ce-m-7',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What is the James Webb Space Telescope primarily designed to observe?',
    options: ['Near-Earth asteroids', 'Infrared light from early universe', 'Solar flares', 'Earth\'s climate'],
    correctAnswer: 1,
    explanation: 'The James Webb Space Telescope observes infrared light, allowing it to see the early universe and through dust clouds.'
  },
  {
    id: 'ce-m-8',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What country hosted the 2022 FIFA World Cup?',
    options: ['Brazil', 'Russia', 'Qatar', 'Saudi Arabia'],
    correctAnswer: 2,
    explanation: 'Qatar hosted the 2022 FIFA World Cup, the first Middle Eastern country to do so.'
  },
  {
    id: 'ce-m-9',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What is the name of China\'s space station?',
    options: ['Shenzhou', 'Tiangong', 'Chang\'e', 'Yutu'],
    correctAnswer: 1,
    explanation: 'Tiangong (meaning "Heavenly Palace") is China\'s modular space station, operational since 2021.'
  },
  {
    id: 'ce-m-10',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What major tech company laid off approximately 11,000 employees in late 2022?',
    options: ['Apple', 'Microsoft', 'Meta (Facebook)', 'Amazon'],
    correctAnswer: 2,
    explanation: 'Meta (Facebook\'s parent company) laid off about 11,000 employees in November 2022, about 13% of its workforce.'
  },
  {
    id: 'ce-m-11',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What is the European Union\'s carbon border adjustment mechanism called?',
    options: ['Green Deal', 'Carbon Tax', 'CBAM', 'ETS'],
    correctAnswer: 2,
    explanation: 'CBAM (Carbon Border Adjustment Mechanism) is the EU\'s system to put a carbon price on imports of carbon-intensive goods.'
  },
  {
    id: 'ce-m-12',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What country became the first in Africa to manufacture COVID-19 vaccines?',
    options: ['Nigeria', 'Kenya', 'South Africa', 'Egypt'],
    correctAnswer: 2,
    explanation: 'South Africa became the first African country to manufacture mRNA COVID-19 vaccines through the Afrigen facility.'
  },
  {
    id: 'ce-m-13',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What is the name of the first commercial space station planned to replace ISS?',
    options: ['Gateway', 'Axiom Station', 'Orbital Reef', 'Voyager Station'],
    correctAnswer: 1,
    explanation: 'Axiom Station is being developed by Axiom Space as a commercial space station to eventually replace the ISS.'
  },
  {
    id: 'ce-m-14',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What cryptocurrency exchange collapsed in November 2022?',
    options: ['Coinbase', 'FTX', 'Binance', 'Kraken'],
    correctAnswer: 1,
    explanation: 'FTX, one of the largest cryptocurrency exchanges, collapsed in November 2022, leading to founder Sam Bankman-Fried\'s arrest.'
  },
  {
    id: 'ce-m-15',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What major Middle East conflict began in October 2023?',
    options: ['Lebanon war', 'Israel-Hamas war', 'Yemen crisis', 'Syria conflict'],
    correctAnswer: 1,
    explanation: 'The Israel-Hamas war began with Hamas attacks on October 7, 2023, leading to ongoing conflict in Gaza.'
  },
  {
    id: 'ce-m-16',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What AI safety concern involves AI generating false information?',
    options: ['Bias', 'Hallucination', 'Alignment', 'Deepfakes'],
    correctAnswer: 1,
    explanation: 'AI hallucination refers to when AI models generate false or nonsensical information presented as fact.'
  },
  {
    id: 'ce-m-17',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What country launched the Chandrayaan-3 mission to the Moon in 2023?',
    options: ['China', 'Japan', 'India', 'South Korea'],
    correctAnswer: 2,
    explanation: 'India successfully landed Chandrayaan-3 on the Moon in August 2023, becoming the fourth country to achieve a soft landing.'
  },
  {
    id: 'ce-m-18',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What is the name of the African Continental Free Trade Area agreement?',
    options: ['ECOWAS', 'AfCFTA', 'SADC', 'AU Trade'],
    correctAnswer: 1,
    explanation: 'AfCFTA (African Continental Free Trade Area) aims to create a single market for goods and services across Africa.'
  },
  {
    id: 'ce-m-19',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What major streaming service launched password-sharing crackdowns in 2023?',
    options: ['Amazon Prime', 'Disney+', 'Netflix', 'Apple TV+'],
    correctAnswer: 2,
    explanation: 'Netflix began enforcing restrictions on password sharing in 2023 to increase subscriber numbers.'
  },
  {
    id: 'ce-m-20',
    category: 'current-events',
    difficulty: 'medium',
    question: 'What does "ESG" stand for in business contexts?',
    options: ['Economic Strategy Group', 'Environmental, Social, Governance', 'Enterprise Software Group', 'Energy Sustainability Goals'],
    correctAnswer: 1,
    explanation: 'ESG stands for Environmental, Social, and Governance - criteria for evaluating corporate responsibility and sustainability.'
  },

  // HARD QUESTIONS (41-60)
  {
    id: 'ce-h-1',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is the EU AI Act\'s risk-based classification system primarily based on?',
    options: ['Computing power', 'Potential harm to fundamental rights', 'Cost of development', 'Industry sector'],
    correctAnswer: 1,
    explanation: 'The EU AI Act classifies AI systems by their risk level to fundamental rights and safety, from unacceptable to minimal risk.'
  },
  {
    id: 'ce-h-2',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What percentage of global greenhouse gas emissions comes from food systems?',
    options: ['About 15%', 'About 25%', 'About 35%', 'About 45%'],
    correctAnswer: 2,
    explanation: 'Food systems contribute approximately 35% of global greenhouse gas emissions, from production to waste.'
  },
  {
    id: 'ce-h-3',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What was the primary mechanism of the FTX cryptocurrency exchange collapse?',
    options: ['Hacking attack', 'Using customer funds for risky investments', 'Government seizure', 'Technical failure'],
    correctAnswer: 1,
    explanation: 'FTX collapsed after it was revealed that customer funds were improperly used for risky investments through Alameda Research.'
  },
  {
    id: 'ce-h-4',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is the primary goal of the Artemis Accords?',
    options: ['Establish military bases on Moon', 'Create principles for civil space exploration', 'Mine lunar resources commercially', 'Build Moon hotels'],
    correctAnswer: 1,
    explanation: 'The Artemis Accords establish principles for peaceful, transparent, and sustainable lunar exploration and cooperation.'
  },
  {
    id: 'ce-h-5',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What technology underpins Large Language Models like ChatGPT?',
    options: ['Recurrent Neural Networks', 'Transformer architecture', 'Convolutional Neural Networks', 'Decision Trees'],
    correctAnswer: 1,
    explanation: 'Large Language Models use the Transformer architecture, introduced in the 2017 paper "Attention Is All You Need".'
  },
  {
    id: 'ce-h-6',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is the primary concern of AI alignment research?',
    options: ['Making AI faster', 'Ensuring AI goals align with human values', 'Reducing AI costs', 'Improving AI accuracy'],
    correctAnswer: 1,
    explanation: 'AI alignment research focuses on ensuring AI systems pursue goals that are beneficial to humanity and aligned with human values.'
  },
  {
    id: 'ce-h-7',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is quantum supremacy (quantum advantage)?',
    options: ['Quantum computers being cheaper', 'Quantum computers solving problems classical computers cannot', 'Quantum computers being smaller', 'Quantum computers using less power'],
    correctAnswer: 1,
    explanation: 'Quantum supremacy is when a quantum computer solves a problem that is practically impossible for classical computers.'
  },
  {
    id: 'ce-h-8',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What major semiconductor manufacturing technology was embargoed to China by the US?',
    options: ['28nm chips', '14nm chips', 'Advanced EUV lithography', '5nm chips'],
    correctAnswer: 2,
    explanation: 'The US restricted China\'s access to advanced EUV (Extreme Ultraviolet) lithography technology critical for cutting-edge chip manufacturing.'
  },
  {
    id: 'ce-h-9',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is the primary mechanism of mRNA vaccines?',
    options: ['Introducing weakened virus', 'Teaching cells to make spike protein', 'Injecting antibodies directly', 'Using dead virus particles'],
    correctAnswer: 1,
    explanation: 'mRNA vaccines deliver genetic instructions for cells to produce the spike protein, triggering an immune response.'
  },
  {
    id: 'ce-h-10',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is the Paris Agreement\'s temperature goal above pre-industrial levels?',
    options: ['Limit to 1.0°C', 'Limit to well below 2°C, preferably 1.5°C', 'Limit to 2.5°C', 'Limit to 3.0°C'],
    correctAnswer: 1,
    explanation: 'The Paris Agreement aims to limit global warming to well below 2°C above pre-industrial levels, preferably to 1.5°C.'
  },
  {
    id: 'ce-h-11',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What does GDPR stand for?',
    options: ['Global Data Protection Regulation', 'General Data Protection Regulation', 'German Data Privacy Rules', 'Global Digital Privacy Rights'],
    correctAnswer: 1,
    explanation: 'GDPR (General Data Protection Regulation) is the EU\'s comprehensive data protection law enacted in 2018.'
  },
  {
    id: 'ce-h-12',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What renewable energy source has seen the fastest cost decline?',
    options: ['Wind power', 'Solar PV', 'Hydroelectric', 'Geothermal'],
    correctAnswer: 1,
    explanation: 'Solar photovoltaic (PV) costs have declined by approximately 90% since 2010, the fastest among renewable energy sources.'
  },
  {
    id: 'ce-h-13',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is the primary concern with deepfake technology?',
    options: ['High production cost', 'Spreading misinformation', 'Legal copyright issues', 'Technical complexity'],
    correctAnswer: 1,
    explanation: 'Deepfakes pose risks for spreading misinformation, fraud, and non-consensual content creation using AI-generated media.'
  },
  {
    id: 'ce-h-14',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is Web3 primarily characterized by?',
    options: ['Faster internet speeds', 'Decentralization and blockchain', 'Better graphics', 'Cloud computing'],
    correctAnswer: 1,
    explanation: 'Web3 refers to a decentralized internet built on blockchain technology, giving users ownership of their data and digital assets.'
  },
  {
    id: 'ce-h-15',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What technology enables CRISPR gene editing?',
    options: ['Viral vectors', 'Guide RNA and Cas9 enzyme', 'Stem cells', 'Recombinant DNA'],
    correctAnswer: 1,
    explanation: 'CRISPR uses guide RNA to direct the Cas9 enzyme to specific DNA sequences for precise gene editing.'
  },
  {
    id: 'ce-h-16',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What percentage of Bitcoin mining uses renewable energy (approximate)?',
    options: ['20%', '40%', '60%', '80%'],
    correctAnswer: 1,
    explanation: 'Studies suggest approximately 40-50% of Bitcoin mining uses renewable energy, though this varies by region and time.'
  },
  {
    id: 'ce-h-17',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is the primary bottleneck in current semiconductor production?',
    options: ['Silicon shortage', 'ASML lithography machines', 'Chip designers', 'Energy costs'],
    correctAnswer: 1,
    explanation: 'ASML\'s EUV lithography machines are critical for advanced chip production, with limited global supply creating a bottleneck.'
  },
  {
    id: 'ce-h-18',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is the "triple planetary crisis" identified by the UN?',
    options: ['War, poverty, disease', 'Climate change, biodiversity loss, pollution', 'Inequality, hunger, education', 'Energy, water, food'],
    correctAnswer: 1,
    explanation: 'The UN identifies climate change, biodiversity loss, and pollution as the interconnected triple planetary crisis.'
  },
  {
    id: 'ce-h-19',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What is the estimated number of parameters in GPT-4?',
    options: ['175 billion', '500 billion', '1 trillion+', 'Not publicly disclosed'],
    correctAnswer: 3,
    explanation: 'OpenAI has not publicly disclosed the exact parameter count of GPT-4, unlike earlier models.'
  },
  {
    id: 'ce-h-20',
    category: 'current-events',
    difficulty: 'hard',
    question: 'What principle guides the EU\'s approach to AI regulation?',
    options: ['Innovation first', 'Risk-based regulation', 'Total ban on AI', 'Self-regulation only'],
    correctAnswer: 1,
    explanation: 'The EU AI Act uses risk-based regulation, with stricter rules for higher-risk AI applications affecting fundamental rights.'
  }
];
const geographyQuestions = [
  // EASY QUESTIONS (1-20)
  {
    id: 'geo-e-1',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the capital city of South Africa?',
    options: ['Johannesburg', 'Cape Town', 'Pretoria', 'All three'],
    correctAnswer: 3,
    explanation: 'South Africa has three capitals: Pretoria (executive), Cape Town (legislative), and Bloemfontein (judicial).'
  },
  {
    id: 'geo-e-2',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the largest continent by land area?',
    options: ['Africa', 'Asia', 'North America', 'Europe'],
    correctAnswer: 1,
    explanation: 'Asia is the largest continent, covering about 44.58 million km² (30% of Earth\'s land area).'
  },
  {
    id: 'geo-e-3',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the longest river in the world?',
    options: ['Amazon', 'Nile', 'Yangtze', 'Mississippi'],
    correctAnswer: 1,
    explanation: 'The Nile River is the longest at approximately 6,650 km, flowing through northeastern Africa.'
  },
  {
    id: 'geo-e-4',
    category: 'geography',
    difficulty: 'easy',
    question: 'How many countries are in Africa?',
    options: ['44', '48', '52', '54'],
    correctAnswer: 3,
    explanation: 'There are 54 recognized countries in Africa.'
  },
  {
    id: 'geo-e-5',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the largest ocean on Earth?',
    options: ['Atlantic Ocean', 'Indian Ocean', 'Pacific Ocean', 'Arctic Ocean'],
    correctAnswer: 2,
    explanation: 'The Pacific Ocean is the largest, covering more than 165 million km² (about 46% of the world\'s ocean surface).'
  },
  {
    id: 'geo-e-6',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the highest mountain in Africa?',
    options: ['Mount Kenya', 'Mount Kilimanjaro', 'Table Mountain', 'Atlas Mountains'],
    correctAnswer: 1,
    explanation: 'Mount Kilimanjaro in Tanzania is Africa\'s highest peak at 5,895 meters above sea level.'
  },
  {
    id: 'geo-e-7',
    category: 'geography',
    difficulty: 'easy',
    question: 'Which country is both in Europe and Asia?',
    options: ['Russia', 'India', 'Egypt', 'Greece'],
    correctAnswer: 0,
    explanation: 'Russia spans both Europe and Asia, with about 77% of its land area in Asia and 23% in Europe.'
  },
  {
    id: 'geo-e-8',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the largest desert in the world?',
    options: ['Sahara', 'Antarctic', 'Arabian', 'Gobi'],
    correctAnswer: 1,
    explanation: 'Antarctica is technically the largest desert (14 million km²) as a cold desert. The Sahara is the largest hot desert.'
  },
  {
    id: 'geo-e-9',
    category: 'geography',
    difficulty: 'easy',
    question: 'What country has the largest population in the world?',
    options: ['United States', 'India', 'China', 'Indonesia'],
    correctAnswer: 1,
    explanation: 'India surpassed China to become the world\'s most populous country in 2023, with over 1.4 billion people.'
  },
  {
    id: 'geo-e-10',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the smallest country in the world by area?',
    options: ['Monaco', 'Vatican City', 'San Marino', 'Liechtenstein'],
    correctAnswer: 1,
    explanation: 'Vatican City is the smallest country at about 0.44 km² (110 acres).'
  },
  {
    id: 'geo-e-11',
    category: 'geography',
    difficulty: 'easy',
    question: 'Which South African province is known as the "Garden Province"?',
    options: ['Gauteng', 'KwaZulu-Natal', 'Western Cape', 'Eastern Cape'],
    correctAnswer: 1,
    explanation: 'KwaZulu-Natal is known as the Garden Province due to its lush vegetation and diverse ecosystems.'
  },
  {
    id: 'geo-e-12',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the capital of Kenya?',
    options: ['Lagos', 'Nairobi', 'Kampala', 'Dar es Salaam'],
    correctAnswer: 1,
    explanation: 'Nairobi is the capital and largest city of Kenya.'
  },
  {
    id: 'geo-e-13',
    category: 'geography',
    difficulty: 'easy',
    question: 'How many provinces does South Africa have?',
    options: ['7', '9', '11', '13'],
    correctAnswer: 1,
    explanation: 'South Africa has 9 provinces: Gauteng, Western Cape, Eastern Cape, KwaZulu-Natal, Limpopo, Mpumalanga, North West, Free State, and Northern Cape.'
  },
  {
    id: 'geo-e-14',
    category: 'geography',
    difficulty: 'easy',
    question: 'What body of water separates Africa from Europe?',
    options: ['Red Sea', 'Mediterranean Sea', 'Atlantic Ocean', 'Arabian Sea'],
    correctAnswer: 1,
    explanation: 'The Mediterranean Sea separates Africa from Europe, with the Strait of Gibraltar at its narrowest point.'
  },
  {
    id: 'geo-e-15',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the capital of Nigeria?',
    options: ['Lagos', 'Abuja', 'Kano', 'Ibadan'],
    correctAnswer: 1,
    explanation: 'Abuja is the capital of Nigeria, replacing Lagos in 1991.'
  },
  {
    id: 'geo-e-16',
    category: 'geography',
    difficulty: 'easy',
    question: 'Which ocean borders South Africa\'s eastern coast?',
    options: ['Atlantic Ocean', 'Indian Ocean', 'Pacific Ocean', 'Arctic Ocean'],
    correctAnswer: 1,
    explanation: 'The Indian Ocean borders South Africa\'s eastern and southern coasts, while the Atlantic borders the western coast.'
  },
  {
    id: 'geo-e-17',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the largest country in Africa by land area?',
    options: ['Nigeria', 'Sudan', 'Algeria', 'Democratic Republic of Congo'],
    correctAnswer: 2,
    explanation: 'Algeria is Africa\'s largest country by area at 2.38 million km².'
  },
  {
    id: 'geo-e-18',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the name of the large plateau region covering much of southern Africa?',
    options: ['Great Rift Valley', 'Highveld', 'Sahel', 'Ethiopian Highlands'],
    correctAnswer: 1,
    explanation: 'The Highveld is the large plateau region in South Africa, at elevations between 1,200-1,800 meters.'
  },
  {
    id: 'geo-e-19',
    category: 'geography',
    difficulty: 'easy',
    question: 'How many lines of latitude are there?',
    options: ['90', '180', '360', '0'],
    correctAnswer: 1,
    explanation: 'There are 180 lines of latitude: 90 in the Northern Hemisphere and 90 in the Southern Hemisphere.'
  },
  {
    id: 'geo-e-20',
    category: 'geography',
    difficulty: 'easy',
    question: 'What is the equator?',
    options: ['The North Pole', 'An imaginary line at 0° latitude', 'The Prime Meridian', 'The South Pole'],
    correctAnswer: 1,
    explanation: 'The equator is an imaginary line at 0° latitude that divides Earth into Northern and Southern Hemispheres.'
  },

  // MEDIUM QUESTIONS (21-40)
  {
    id: 'geo-m-1',
    category: 'geography',
    difficulty: 'medium',
    question: 'What is the name of the mountain range that runs through South Africa, Lesotho, and Eswatini?',
    options: ['Atlas Mountains', 'Drakensberg Mountains', 'Cape Fold Mountains', 'Magaliesberg'],
    correctAnswer: 1,
    explanation: 'The Drakensberg (Dragon Mountains) is the highest mountain range in southern Africa, reaching over 3,000 meters.'
  },
  {
    id: 'geo-m-2',
    category: 'geography',
    difficulty: 'medium',
    question: 'What percentage of Earth\'s surface is covered by land?',
    options: ['19%', '29%', '39%', '49%'],
    correctAnswer: 1,
    explanation: 'Approximately 29% of Earth\'s surface is land, while 71% is covered by water.'
  },
  {
    id: 'geo-m-3',
    category: 'geography',
    difficulty: 'medium',
    question: 'What is the name of the desert that stretches across Botswana, Namibia, and South Africa?',
    options: ['Sahara', 'Namib', 'Kalahari', 'Karoo'],
    correctAnswer: 2,
    explanation: 'The Kalahari Desert covers much of Botswana and extends into Namibia and South Africa.'
  },
  {
    id: 'geo-m-4',
    category: 'geography',
    difficulty: 'medium',
    question: 'What is the deepest point in Earth\'s oceans?',
    options: ['Puerto Rico Trench', 'Mariana Trench', 'Java Trench', 'Tonga Trench'],
    correctAnswer: 1,
    explanation: 'The Mariana Trench\'s Challenger Deep is the deepest point at approximately 11,034 meters below sea level.'
  },
  {
    id: 'geo-m-5',
    category: 'geography',
    difficulty: 'medium',
    question: 'Which African country is closest to Europe?',
    options: ['Algeria', 'Tunisia', 'Morocco', 'Libya'],
    correctAnswer: 2,
    explanation: 'Morocco is closest to Europe, separated from Spain by just 14 km at the Strait of Gibraltar.'
  },
  {
    id: 'geo-m-6',
    category: 'geography',
    difficulty: 'medium',
    question: 'What is the largest island in Africa?',
    options: ['Zanzibar', 'Madagascar', 'Mauritius', 'Seychelles'],
    correctAnswer: 1,
    explanation: 'Madagascar is the largest African island and the fourth largest island in the world at 587,041 km².'
  },
  {
    id: 'geo-m-7',
    category: 'geography',
    difficulty: 'medium',
    question: 'What major river flows through Egypt?',
    options: ['Nile', 'Niger', 'Congo', 'Zambezi'],
    correctAnswer: 0,
    explanation: 'The Nile River flows through Egypt, providing water to this desert nation and supporting most of its population.'
  },
  {
    id: 'geo-m-8',
    category: 'geography',
    difficulty: 'medium',
    question: 'At what latitude is the Tropic of Capricorn?',
    options: ['23.5°N', '23.5°S', '66.5°N', '66.5°S'],
    correctAnswer: 1,
    explanation: 'The Tropic of Capricorn is at approximately 23.5°S latitude and passes through southern Africa, Australia, and South America.'
  },
  {
    id: 'geo-m-9',
    category: 'geography',
    difficulty: 'medium',
    question: 'What is the name of the famous waterfall on the Zambezi River?',
    options: ['Angel Falls', 'Victoria Falls', 'Niagara Falls', 'Iguazu Falls'],
    correctAnswer: 1,
    explanation: 'Victoria Falls, known locally as Mosi-oa-Tunya ("The Smoke That Thunders"), is one of the world\'s largest waterfalls.'
  },
  {
    id: 'geo-m-10',
    category: 'geography',
    difficulty: 'medium',
    question: 'Which South African city is known as the "City of Gold"?',
    options: ['Cape Town', 'Johannesburg', 'Durban', 'Pretoria'],
    correctAnswer: 1,
    explanation: 'Johannesburg is called the "City of Gold" due to the gold mining industry that led to its establishment in 1886.'
  },
  {
    id: 'geo-m-11',
    category: 'geography',
    difficulty: 'medium',
    question: 'What percentage of the world\'s population lives in the Northern Hemisphere?',
    options: ['50%', '68%', '80%', '90%'],
    correctAnswer: 3,
    explanation: 'Approximately 90% of the world\'s population lives in the Northern Hemisphere.'
  },
  {
    id: 'geo-m-12',
    category: 'geography',
    difficulty: 'medium',
    question: 'What is the capital of Ethiopia?',
    options: ['Nairobi', 'Addis Ababa', 'Khartoum', 'Kampala'],
    correctAnswer: 1,
    explanation: 'Addis Ababa is the capital of Ethiopia and also hosts the headquarters of the African Union.'
  },
  {
    id: 'geo-m-13',
    category: 'geography',
    difficulty: 'medium',
    question: 'Which African Great Lake is the deepest?',
    options: ['Lake Victoria', 'Lake Tanganyika', 'Lake Malawi', 'Lake Turkana'],
    correctAnswer: 1,
    explanation: 'Lake Tanganyika is the deepest African lake (1,470 meters) and the second-deepest lake in the world.'
  },
  {
    id: 'geo-m-14',
    category: 'geography',
    difficulty: 'medium',
    question: 'What is the name of the strait between South Africa and Australia?',
    options: ['Drake Passage', 'Bering Strait', 'There is no strait; they are separated by the Indian Ocean', 'Mozambique Channel'],
    correctAnswer: 2,
    explanation: 'South Africa and Australia are separated by the Indian Ocean, not a strait. The Mozambique Channel is between Africa and Madagascar.'
  },
  {
    id: 'geo-m-15',
    category: 'geography',
    difficulty: 'medium',
    question: 'Which country has the most UNESCO World Heritage Sites?',
    options: ['France', 'China', 'Italy', 'Spain'],
    correctAnswer: 2,
    explanation: 'Italy has the most UNESCO World Heritage Sites with 58 sites as of 2023.'
  },
  {
    id: 'geo-m-16',
    category: 'geography',
    difficulty: 'medium',
    question: 'What is the name of the tectonic valley running through East Africa?',
    options: ['Great Rift Valley', 'Nile Valley', 'Zambezi Valley', 'Congo Basin'],
    correctAnswer: 0,
    explanation: 'The Great Rift Valley runs from the Red Sea down through East Africa, formed by tectonic plate divergence.'
  },
  {
    id: 'geo-m-17',
    category: 'geography',
    difficulty: 'medium',
    question: 'What South African city is known as the "Mother City"?',
    options: ['Johannesburg', 'Pretoria', 'Cape Town', 'Port Elizabeth'],
    correctAnswer: 2,
    explanation: 'Cape Town is known as the "Mother City" as it was the first European settlement in South Africa.'
  },
  {
    id: 'geo-m-18',
    category: 'geography',
    difficulty: 'medium',
    question: 'Which river forms the border between South Africa and Zimbabwe?',
    options: ['Orange River', 'Vaal River', 'Limpopo River', 'Tugela River'],
    correctAnswer: 2,
    explanation: 'The Limpopo River forms part of the border between South Africa and Zimbabwe (and Botswana).'
  },
  {
    id: 'geo-m-19',
    category: 'geography',
    difficulty: 'medium',
    question: 'What is the second-largest continent by population?',
    options: ['Asia', 'Africa', 'Europe', 'North America'],
    correctAnswer: 1,
    explanation: 'Africa is the second-most populous continent with over 1.4 billion people (after Asia with over 4.7 billion).'
  },
  {
    id: 'geo-m-20',
    category: 'geography',
    difficulty: 'medium',
    question: 'What is the largest city in Africa by population?',
    options: ['Cairo', 'Lagos', 'Kinshasa', 'Johannesburg'],
    correctAnswer: 0,
    explanation: 'Cairo, Egypt is Africa\'s largest city with a metropolitan population of over 20 million people.'
  },

  // HARD QUESTIONS (41-60)
  {
    id: 'geo-h-1',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is the name of the ocean current that flows along South Africa\'s east coast?',
    options: ['Benguela Current', 'Agulhas Current', 'Antarctic Circumpolar Current', 'Mozambique Current'],
    correctAnswer: 1,
    explanation: 'The Agulhas Current is a warm ocean current flowing along South Africa\'s east coast, one of the fastest in the world.'
  },
  {
    id: 'geo-h-2',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is the approximate population density of Africa (people per km²)?',
    options: ['25', '45', '65', '85'],
    correctAnswer: 1,
    explanation: 'Africa has a population density of approximately 45 people per km², relatively low compared to Asia (150) or Europe (75).'
  },
  {
    id: 'geo-h-3',
    category: 'geography',
    difficulty: 'hard',
    question: 'What geological formation is Lesotho known for?',
    options: ['Volcanic mountains', 'Being entirely above 1,000m elevation', 'Limestone caves', 'Coral reefs'],
    correctAnswer: 1,
    explanation: 'Lesotho is unique as the only country entirely above 1,000 meters elevation. Its lowest point is 1,400m, highest is 3,482m.'
  },
  {
    id: 'geo-h-4',
    category: 'geography',
    difficulty: 'hard',
    question: 'Which line of longitude is opposite the Prime Meridian?',
    options: ['90° E', '180° E/W', '90° W', '270° E'],
    correctAnswer: 1,
    explanation: 'The 180° meridian (International Date Line) is directly opposite the Prime Meridian (0°).'
  },
  {
    id: 'geo-h-5',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is the name of the ocean current that cools South Africa\'s west coast?',
    options: ['Agulhas Current', 'Benguela Current', 'Canary Current', 'Guinea Current'],
    correctAnswer: 1,
    explanation: 'The Benguela Current is a cold upwelling current flowing northward along South Africa\'s west coast.'
  },
  {
    id: 'geo-h-6',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is the approximate age of the Karoo Supergroup rock formations?',
    options: ['50 million years', '150 million years', '300 million years', '1 billion years'],
    correctAnswer: 2,
    explanation: 'The Karoo Supergroup rocks date from about 320 to 180 million years ago (Carboniferous to Jurassic periods).'
  },
  {
    id: 'geo-h-7',
    category: 'geography',
    difficulty: 'hard',
    question: 'How many countries does the Nile River flow through?',
    options: ['5', '7', '9', '11'],
    correctAnswer: 3,
    explanation: 'The Nile flows through or borders 11 countries: Burundi, Rwanda, Tanzania, Uganda, Kenya, DRC, Ethiopia, Eritrea, Sudan, South Sudan, and Egypt.'
  },
  {
    id: 'geo-h-8',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is the driest place on Earth?',
    options: ['Sahara Desert', 'Atacama Desert', 'McMurdo Dry Valleys, Antarctica', 'Death Valley'],
    correctAnswer: 2,
    explanation: 'The McMurdo Dry Valleys in Antarctica are Earth\'s driest place, with some areas receiving no precipitation for nearly 2 million years.'
  },
  {
    id: 'geo-h-9',
    category: 'geography',
    difficulty: 'hard',
    question: 'What percentage of Africa\'s land area is arable (suitable for farming)?',
    options: ['5%', '10%', '20%', '30%'],
    correctAnswer: 1,
    explanation: 'Only about 10% of Africa\'s land is arable, compared to the global average of about 11%.'
  },
  {
    id: 'geo-h-10',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is the name of the boundary between tectonic plates running through the Atlantic Ocean?',
    options: ['San Andreas Fault', 'Mid-Atlantic Ridge', 'Ring of Fire', 'East African Rift'],
    correctAnswer: 1,
    explanation: 'The Mid-Atlantic Ridge is a divergent boundary where the Eurasian and North American plates (and others) are spreading apart.'
  },
  {
    id: 'geo-h-11',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is South Africa\'s approximate land area in km²?',
    options: ['800,000', '1,000,000', '1,219,000', '1,500,000'],
    correctAnswer: 2,
    explanation: 'South Africa has a land area of approximately 1,219,090 km², making it the 24th-largest country in the world.'
  },
  {
    id: 'geo-h-12',
    category: 'geography',
    difficulty: 'hard',
    question: 'What mineral resource is the Witwatersrand Basin famous for?',
    options: ['Diamonds', 'Gold', 'Platinum', 'Coal'],
    correctAnswer: 1,
    explanation: 'The Witwatersrand Basin contains the world\'s largest known gold reserves, having produced about 40% of all gold ever mined.'
  },
  {
    id: 'geo-h-13',
    category: 'geography',
    difficulty: 'hard',
    question: 'What biome covers the largest area of South Africa?',
    options: ['Fynbos', 'Grassland', 'Savanna', 'Forest'],
    correctAnswer: 2,
    explanation: 'Savanna (bushveld) covers the largest area of South Africa, about 46% of the country\'s land area.'
  },
  {
    id: 'geo-h-14',
    category: 'geography',
    difficulty: 'hard',
    question: 'How many time zones does Africa span?',
    options: ['4', '5', '6', '7'],
    correctAnswer: 2,
    explanation: 'Africa spans 6 time zones from UTC-1 (Cape Verde) to UTC+4 (Mauritius and Seychelles).'
  },
  {
    id: 'geo-h-15',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is the name of the mountain range in northwestern Africa?',
    options: ['Drakensberg', 'Atlas Mountains', 'Ethiopian Highlands', 'Tibesti Mountains'],
    correctAnswer: 1,
    explanation: 'The Atlas Mountains stretch through Morocco, Algeria, and Tunisia, reaching heights over 4,000 meters.'
  },
  {
    id: 'geo-h-16',
    category: 'geography',
    difficulty: 'hard',
    question: 'What percentage of Earth\'s fresh water is frozen in ice caps and glaciers?',
    options: ['50%', '60%', '69%', '80%'],
    correctAnswer: 2,
    explanation: 'Approximately 69% of Earth\'s fresh water is locked in ice caps, glaciers, and permanent snow.'
  },
  {
    id: 'geo-h-17',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is the approximate elevation of Johannesburg above sea level?',
    options: ['1,200m', '1,500m', '1,753m', '2,000m'],
    correctAnswer: 2,
    explanation: 'Johannesburg sits at approximately 1,753 meters above sea level, making it one of the world\'s highest major cities.'
  },
  {
    id: 'geo-h-18',
    category: 'geography',
    difficulty: 'hard',
    question: 'What geological era is the Cradle of Humankind\'s fossils primarily from?',
    options: ['Jurassic', 'Cretaceous', 'Pliocene-Pleistocene', 'Holocene'],
    correctAnswer: 2,
    explanation: 'The Cradle of Humankind fossils are primarily from the Pliocene-Pleistocene era (3.5 million to 1 million years ago).'
  },
  {
    id: 'geo-h-19',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is the approximate length of Africa\'s coastline?',
    options: ['20,000 km', '26,000 km', '30,500 km', '35,000 km'],
    correctAnswer: 2,
    explanation: 'Africa\'s coastline is approximately 30,500 km long, surprisingly short relative to its size due to limited indentation.'
  },
  {
    id: 'geo-h-20',
    category: 'geography',
    difficulty: 'hard',
    question: 'What is the dominant wind system affecting South Africa\'s weather?',
    options: ['Trade winds', 'Westerlies', 'Monsoons', 'Polar easterlies'],
    correctAnswer: 1,
    explanation: 'The westerlies dominate South Africa\'s weather, particularly in the south and west, bringing winter rainfall to the Western Cape.'
  }
];

// Export all questions
export const allQuestions = [
  ...southAfricanHistoryQuestions,
  ...pythonProgrammingQuestions,
  ...aiLiteracyQuestions,
  ...generalScienceQuestions,
  ...mathematicsQuestions,
  ...currentEventsQuestions,
  ...geographyQuestions
];

// Helper functions
export function getQuestionsByCategory(categoryId) {
  return allQuestions.filter(q => q.category === categoryId);
}

export function getQuestionsByDifficulty(categoryId, difficulty) {
  return allQuestions.filter(q => q.category === categoryId && q.difficulty === difficulty);
}

export function getRandomQuestions(categoryId, difficulty, count) {
  const questions = getQuestionsByDifficulty(categoryId, difficulty);
  const shuffled = [...questions].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

export function getCategoryStats(categoryId) {
  const questions = getQuestionsByCategory(categoryId);
  return {
    total: questions.length,
    easy: questions.filter(q => q.difficulty === 'easy').length,
    medium: questions.filter(q => q.difficulty === 'medium').length,
    hard: questions.filter(q => q.difficulty === 'hard').length
  };
}
